import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCar,
  FaPercent,
  FaStickyNote,
  FaEuroSign,
  FaCreditCard,
  FaTachometerAlt,
  FaGasPump,
  FaClipboardCheck,
  FaCamera,
  FaUpload,
  FaSignature,
  FaArrowRight,
  FaArrowLeft,
  FaCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPlus,
  FaTrash,
  FaClipboardList,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Import services
import {
  cars,
  coupons,
  rentals,
  locations,
  additionalOptions,
  handoverService,
  oxygen,
  employeeService,
} from "../../services/api";

// Import car images (same as CreateRentalModal)
import OpelCorsa from "../../assets/images/opelcorsa.jpg";
import SpaceStar from "../../assets/images/MITS-SPACE.png";
import Peugeot208 from "../../assets/images/p-208.jpg";
import Peugeot108 from "../../assets/images/108-p.webp";
import CitroenC1 from "../../assets/images/C1.jpeg";
import Micra from "../../assets/images/PHOTO-2022-01-23-14-12-06.jpg";
import Panda from "../../assets/images/PHOTO-2022-01-23-14-12-05-1.jpg";
import Stonic from "../../assets/images/KIA-STONIC-Manual-1.png";
import i20 from "../../assets/images/HYUNDAI-i20-Manual-1.png";
import quashqai from "../../assets/images/quashqai.jpg";
import duster from "../../assets/images/duster.jpeg";
import crossland from "../../assets/images/opel-crossland.webp";
import clio from "../../assets/images/RENAULT-CLIO-Manual-1.png";
import rio from "../../assets/images/KIA-RIO-Auto-1.png";
import Wrangler5D from "../../assets/images/jeep-wr-5d.png";
import WranglerCabrio from "../../assets/images/Jeep-wrangler-cabrio.png";
import Discovery from "../../assets/images/land-rover.webp";
import Wrangler from "../../assets/images/JEEP-WRANGLER-Auto-1.png";
import WranglerSahara from "../../assets/images/JEEP-WRANGLER-1.png.webp";
import Berlingo from "../../assets/images/CITROEN-BERLINGO-Auto.png.webp";
import Doblo from "../../assets/images/FIAT-DOBLO-Manual-1.png.webp";
import TRoc from "../../assets/images/vw-t-roc.png";
import C1Cabrio from "../../assets/images/PHOTO-2022-01-23-14-12-11.jpg.webp";
import MiniCooper from "../../assets/images/MINI-COOPER-Cabrio-Auto-1.png.webp";
import Fiat500 from "../../assets/images/FIAT-500-Auto-Cabrio-1.png.webp";
import SmartForTwo from "../../assets/images/SMART-ForTwo-Cabrio-Manual-4.png.webp";
import SmartForTwoAuto from "../../assets/images/SMART-ForTwo-Cabrio-Auto-2.png.webp";
import Buggy800 from "../../assets/images/CF-MOTO-BUGGY-800cc-1.png.webp";
import Buggy550 from "../../assets/images/beach_buggy.png";
import ATV450 from "../../assets/images/5_14f72470629695dca3487f902b2c012f.png.webp";
import RangeSport from "../../assets/images/PHOTO-2022-01-22-12-09-42.jpg";
import C3Aircross from "../../assets/images/PHOTO-2022-01-25-15-13-22.jpg";
import C3 from "../../assets/images/PHOTO-2022-01-22-11-45-44-1.jpg";
import C4Cactus from "../../assets/images/PHOTO-2022-01-22-11-51-47.jpg";
import MiniCountryman from "../../assets/images/PHOTO-2022-01-25-15-13-22-1.jpg";
import Glb2022 from "../../assets/images/MERCEDES-BENZ-GLB-2022-Automatic-1.png";
import Gla2023 from "../../assets/images/253_9060274dbdc76eb56b4a456a70298fff.png";
import BmwX1 from "../../assets/images/BMW-X1-2023-Automatic.png";
import BmwX2 from "../../assets/images/BMW-X2-Automatic-1.png";
import Sportage from "../../assets/images/KIA-SPORTAGE-Auto-1.png";
import Cayenne from "../../assets/images/PORSCHE-CAYENNE-Auto-1.png";
import Bike125 from "../../assets/images/125bike.png";
import Picanto from "../../assets/images/picanto.jpg";
import logo from "../../assets/images/logo-1.png";

// Image map
const imageMap = {
  "opelcorsa.jpg": OpelCorsa,
  "MITS-SPACE.png": SpaceStar,
  "p-208.jpg": Peugeot208,
  "108-p.webp": Peugeot108,
  "C1.jpeg": CitroenC1,
  "PHOTO-2022-01-23-14-12-06.jpg": Micra,
  "PHOTO-2022-01-23-14-12-05-1.jpg": Panda,
  "KIA-STONIC-Manual-1.png": Stonic,
  "HYUNDAI-i20-Manual-1.png": i20,
  "quashqai.jpg": quashqai,
  "duster.jpeg": duster,
  "opel-crossland.webp": crossland,
  "RENAULT-CLIO-Manual-1.png": clio,
  "KIA-RIO-Auto-1.png": rio,
  "jeep-wr-5d.png": Wrangler5D,
  "Jeep-wrangler-cabrio.png": WranglerCabrio,
  "land-rover.webp": Discovery,
  "JEEP-WRANGLER-Auto-1.png": Wrangler,
  "JEEP-WRANGLER-1.png.webp": WranglerSahara,
  "CITROEN-BERLINGO-Auto.png.webp": Berlingo,
  "FIAT-DOBLO-Manual-1.png.webp": Doblo,
  "vw-t-roc.png": TRoc,
  "PHOTO-2022-01-23-14-12-11.jpg.webp": C1Cabrio,
  "MINI-COOPER-Cabrio-Auto-1.png.webp": MiniCooper,
  "FIAT-500-Auto-Cabrio-1.png.webp": Fiat500,
  "SMART-ForTwo-Cabrio-Manual-4.png.webp": SmartForTwo,
  "SMART-ForTwo-Cabrio-Auto-2.png.webp": SmartForTwoAuto,
  "CF-MOTO-BUGGY-800cc-1.png.webp": Buggy800,
  "beach_buggy.png": Buggy550,
  "5_14f72470629695dca3487f902b2c012f.png.webp": ATV450,
  "PHOTO-2022-01-22-12-09-42.jpg": RangeSport,
  "PHOTO-2022-01-25-15-13-22.jpg": C3Aircross,
  "PHOTO-2022-01-22-11-45-44-1.jpg": C3,
  "PHOTO-2022-01-22-11-51-47.jpg": C4Cactus,
  "PHOTO-2022-01-25-15-13-22-1.jpg": MiniCountryman,
  "MERCEDES-BENZ-GLB-2022-Automatic-1.png": Glb2022,
  "253_9060274dbdc76eb56b4a456a70298fff.png": Gla2023,
  "BMW-X1-2023-Automatic.png": BmwX1,
  "BMW-X2-Automatic-1.png": BmwX2,
  "KIA-SPORTAGE-Auto-1.png": Sportage,
  "PORSCHE-CAYENNE-Auto-1.png": Cayenne,
  "125bike.png": Bike125,
  "picanto.jpg": Picanto,
};

const getImage = (imagePath) => {
  if (!imagePath) return null;
  if (typeof imagePath === "string") {
    if (imagePath.startsWith("/images/")) {
      const filename = imagePath.split("/").pop();
      return imageMap[filename] || imagePath; // Fallback to original path if not in map
    }
    const filename = imagePath.split("/").pop();
    return imageMap[filename] || imagePath; // Fallback for static imports
  }
  return imagePath; // Should not happen if data is consistent
};

const CombinedRentalHandoverModal = ({
  isOpen,
  onClose,
  onSuccess,
  selectedDate,
}) => {
  // Current step: 1 = rental creation, 2 = handover form
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createdRental, setCreatedRental] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [availableCars, setAvailableCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [couponStatus, setCouponStatus] = useState({
    isValid: false,
    discount: 0,
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [additionalOptionsData, setAdditionalOptionsData] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false,
  });
  const [dateTimeComplete, setDateTimeComplete] = useState({
    pickup: false,
    dropoff: false,
  });
  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 5 },
    longTerm: { discount: 10 },
  });
  const [sameLocation, setSameLocation] = useState(true);

  const [loadingAdditionalOptions, setLoadingAdditionalOptions] =
    useState(false);

  // Default location options
  const defaultLocationOptions = [
    { value: "airport", label: "Airport of Mykonos (JMK)" },
    { value: "central", label: "Mykonos Central Office" },
    { value: "new-port", label: "Mykonos New Port (Tourlos)" },
    { value: "old-port", label: "Mykonos Old Port" },
    { value: "other", label: "Other Location (Hotel/Restaurant/Beach)" },
  ];

  const [locationOptions, setLocationOptions] = useState(
    defaultLocationOptions
  );

    const [wantsInvoice, setWantsInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState({
      companyName: "",
      vatNumber: "",
      address: "",
      city: "",
      postalCode: "",
      country: "Greece"
    });

  // Admin-specific states
  const [isAdmin, setIsAdmin] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const [rentalFormData, setRentalFormData] = useState({
    pickupDate: null,
    dropoffDate: null,
    pickupLocation: "",
    dropoffLocation: "",
    customPickupLocation: "",
    customDropoffLocation: "",
    car: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "26+",
    additionalOptions: {},
    couponCode: "",
    notes: "",
    customPricePerDay: "",
    advancePayment: "",
    paymentMethod: "",
  });

  // Enhanced handover form states
  const [handoverCurrentStep, setHandoverCurrentStep] = useState(1);
  const handoverTotalSteps = 4;
  const [handoverStepErrors, setHandoverStepErrors] = useState({
    step1: null,
    step2: null,
    step3: null,
    step4: null,
  });

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 },
    },
  };

  const [handoverFormData, setHandoverFormData] = useState({
    handoverType: "pickup",
    mileage: "",
    fuelLevel: "full",
    condition: {
      exterior: "good",
      interior: "good",
      notes: "",
    },
    notes: "",
    damages: [],
    confirmed: false,
  });

  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [signatureData, setSignatureData] = useState({
    employee: "",
    customer: "",
  });

  // Signature refs and states
  const employeeSignatureRef = useRef(null);
  const customerSignatureRef = useRef(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signingMode, setSigningMode] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState({
    employee: false,
    customer: false,
  });

  const fileInputRef = useRef(null);

  // Admin detection and employee fetching
  useEffect(() => {
    const checkAdminAndFetchEmployees = async () => {
      try {
        // Check if user is admin
        const adminToken = localStorage.getItem("adminToken");
        console.log("Admin token:", adminToken);
        const isAdminUser = !!adminToken;
        setIsAdmin(isAdminUser);

        // If admin, fetch employees
        if (isAdminUser) {
          const employeesResponse = await employeeService.getAllEmployees();
          setEmployees(employeesResponse.data);
          console.log("Fetched employees for admin:", employeesResponse.data);
        }
      } catch (err) {
        console.error(
          "Error checking admin status or fetching employees:",
          err
        );
      }
    };

    checkAdminAndFetchEmployees();
  }, []);

  console.log("Admin token:", isAdmin);

  // --- Custom Select and Checkbox Components ---
  const CustomSelect = ({
    value,
    onChange,
    onOtherLocationChange,
    options,
    placeholder,
    showOtherInput = false,
    initialCustomValue = "",
    disabled = false,
  }) => {
    const [showOtherInputField, setShowOtherInputField] = useState(
      value === "other"
    );
    // Add local state to manage input value
    const [localInputValue, setLocalInputValue] = useState(
      initialCustomValue || ""
    );

    // Update local state when prop changes
    useEffect(() => {
      setLocalInputValue(initialCustomValue || "");
    }, [initialCustomValue]);

    useEffect(() => {
      setShowOtherInputField(value === "other" && showOtherInput);
    }, [value, showOtherInput]);

    // Handle local input changes without immediate parent updates
    const handleLocalInputChange = (e) => {
      setLocalInputValue(e.target.value);
    };

    // Update parent component only when focus is lost or Enter is pressed
    const handleInputBlur = () => {
      onOtherLocationChange(localInputValue);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onOtherLocationChange(localInputValue);
      }
    };

    return (
      <div>
        <div className="relative">
          <select
            value={value}
            onChange={(e) => {
              onChange(e);
              setShowOtherInputField(
                e.target.value === "other" && showOtherInput
              );
            }}
            disabled={disabled}
            className={`w-full h-10 pl-3 pr-10 border border-gray-300 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
              disabled ? "bg-gray-100 cursor-not-allowed opacity-70" : "" // Style for disabled
            }`}
          >
            <option value="">{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>
        {showOtherInputField && (
          <input
            type="text"
            placeholder="Enter location details"
            value={localInputValue}
            onChange={handleLocalInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={`w-full h-10 px-3 mt-2 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              disabled ? "bg-gray-100 cursor-not-allowed opacity-70" : "" // Style for disabled
            }`}
          />
        )}
      </div>
    );
  };

  // --- Custom Checkbox Component ---
  const CustomCheckbox = ({
    id,
    checked,
    onChange,
    label,
    disabled = false,
  }) => (
    <label
      htmlFor={id}
      className={`flex items-center group p-2 rounded-md transition-colors ${
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:bg-gray-50"
      }`}
    >
      <div className="relative w-5 h-5 mr-3">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="opacity-0 absolute h-5 w-5 cursor-pointer"
        />
        <div
          className={`border-2 rounded-sm h-5 w-5 flex flex-shrink-0 justify-center items-center ${
            checked ? "bg-blue-500 border-blue-500" : "border-gray-300"
          } ${
            disabled
              ? "bg-gray-200 border-gray-300"
              : "focus-within:border-blue-500"
          }`}
          aria-hidden="true" // Hide decorative element from screen readers
        >
          {checked && (
            <svg
              className={`fill-current w-3 h-3 pointer-events-none ${
                disabled ? "text-gray-400" : "text-white"
              }`}
              viewBox="0 0 20 20"
            >
              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
            </svg>
          )}
        </div>
      </div>
      <span
        className={`select-none ${
          disabled ? "text-gray-500" : "text-gray-700"
        }`}
      >
        {label}
      </span>
    </label>
  );

  // --- Filter Cars based on Search and Category ---
  const filteredCars = availableCars.filter((car) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      `${car.make} ${car.model}`.toLowerCase().includes(searchLower) ||
      car.category?.toLowerCase().includes(searchLower); // Include category in search
    const matchesCategory =
      !selectedCategory || car.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const response = await oxygen.getPaymentMethods();

      if (response && response.data) {
        setPaymentMethods(response.data);
      } else {
        console.error("Invalid payment methods response:", response);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const renderRentalForm = () => {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* --- Car Selection Column --- */}
        <div className="lg:col-span-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            1. Select a Car
          </h3>
          {/* Search and Filter */}
          <div className="sticky top-0 bg-white z-10 p-4 mb-4 border border-gray-200 rounded-lg shadow-sm flex-shrink-0">
            <div className="space-y-3">
              <input
                type="search"
                placeholder="Search make, model, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Search available cars"
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Filter by car category"
              >
                <option value="">All Categories</option>
                {Array.from(new Set(availableCars.map((car) => car.category)))
                  .sort()
                  .map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
              </select>
            </div>
            {errors.car && (
              <p className="mt-2 text-sm text-red-600">{errors.car}</p>
            )}
          </div>
          {/* Car List */}
          <div className="flex-grow overflow-y-auto border border-gray-200 rounded-lg min-h-[300px] max-h-[1200px]">
            <div className="divide-y divide-gray-200">
              {filteredCars.length > 0 ? (
                filteredCars.map((car) => (
                  <div
                    key={car._id}
                    className={`p-4 cursor-pointer hover:bg-gray-100 transition-colors duration-150 ${
                      selectedCar?._id === car._id
                        ? "bg-blue-50 border-l-4 border-blue-600"
                        : "border-l-4 border-transparent"
                    }`}
                    onClick={() => handleCarSelect(car)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCarSelect(car)
                    }
                    aria-pressed={selectedCar?._id === car._id}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 h-16 w-20 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden border">
                        <img
                          src={
                            getImage(car.images?.[0]) ||
                            "https://placehold.co/80x64/e2e8f0/94a3b8?text=No+Image"
                          }
                          alt={`${car.make} ${car.model}`}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src =
                              "https://placehold.co/80x64/e2e8f0/94a3b8?text=Error";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {car.make} {car.model}
                        </p>
                        <p className="text-xs text-gray-500">
                          {car.year} • {car.category}
                        </p>
                        <p className="text-sm font-bold text-blue-800 mt-1">
                          €{car.basePrice}/day
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No cars found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* --- Rental Details Column --- */}
        <div className="lg:col-span-2 space-y-6">
          <h3 className="text-lg font-semibold text-gray-800">
            2. Rental Details{" "}
            {selectedCar ? `for ${selectedCar.make} ${selectedCar.model}` : ""}
          </h3>

          {/* Show message if no car selected */}
          {!selectedCar ? (
            <div
              className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md"
              role="alert"
            >
              <p className="font-medium">
                Please select a car from the list to continue.
              </p>
            </div>
          ) : (
            <>
              {/* --- Date Selection --- */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-medium text-gray-700 mb-3 text-base">
                  Rental Period
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pickup Date */}
                  <div>
                    <label
                      htmlFor="pickupDate"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Pick-up Date & Time *
                    </label>{" "}
                    <DatePicker
                      id="pickupDate"
                      selected={rentalFormData.pickupDate}
                      onChange={(date) => handleDateChange(date, "pickupDate")}
                      onMonthChange={handleMonthChange}
                      dateFormat="dd/MM/yyyy HH:mm"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={30}
                      selectsStart
                      startDate={rentalFormData.pickupDate}
                      endDate={rentalFormData.dropoffDate}
                      placeholderText="Select Pick-up"
                      open={datePickerOpen.pickup}
                      onClickOutside={() => {
                        if (
                          dateTimeComplete.pickup ||
                          !rentalFormData.pickupDate
                        ) {
                          setDatePickerOpen((prev) => ({
                            ...prev,
                            pickup: false,
                          }));
                          if (dateTimeComplete.pickup)
                            setTimeWarnings((prev) => ({
                              ...prev,
                              pickup: false,
                            }));
                        } else {
                          setTimeWarnings((prev) => ({
                            ...prev,
                            pickup: true,
                          }));
                        }
                      }}
                      wrapperClassName="w-full"
                      onInputClick={() =>
                        setDatePickerOpen((prev) => ({
                          ...prev,
                          pickup: true,
                          dropoff: false,
                        }))
                      }
                      renderDayContents={renderDayContents}
                      disabled={!selectedCar}
                      popperPlacement="bottom-start"
                      required
                      aria-required="true"
                      aria-describedby="pickupDateError pickupTimeWarning"
                      className={`w-full px-3 py-2 border ${
                        errors.pickupDate
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.pickupDate && (
                      <p
                        id="pickupDateError"
                        className="text-red-600 text-xs mt-1"
                      >
                        {errors.pickupDate}
                      </p>
                    )}
                    {timeWarnings.pickup && !dateTimeComplete.pickup && (
                      <p
                        id="pickupTimeWarning"
                        className="text-yellow-600 text-xs mt-1"
                      >
                        Please select a time.
                      </p>
                    )}
                  </div>
                  {/* Dropoff Date */}
                  <div>
                    <label
                      htmlFor="dropoffDate"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Drop-off Date & Time *
                    </label>
                    <DatePicker
                      id="dropoffDate"
                      selected={rentalFormData.dropoffDate}
                      onChange={(date) => handleDateChange(date, "dropoffDate")}
                      onMonthChange={handleMonthChange}
                      dateFormat="dd/MM/yyyy HH:mm"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={30}
                      minDate={rentalFormData.pickupDate}
                      filterTime={(time) => {
                        if (!rentalFormData.pickupDate) return true;
                        const pickupD = new Date(rentalFormData.pickupDate);
                        const currentDropoffDate =
                          rentalFormData.dropoffDate || new Date();
                        const timeToCheck = new Date(currentDropoffDate);
                        timeToCheck.setHours(
                          time.getHours(),
                          time.getMinutes(),
                          0,
                          0
                        );

                        if (
                          timeToCheck.toDateString() === pickupD.toDateString()
                        ) {
                          return timeToCheck.getTime() > pickupD.getTime();
                        }
                        return true;
                      }}
                      selectsEnd
                      startDate={rentalFormData.pickupDate}
                      endDate={rentalFormData.dropoffDate}
                      placeholderText="Select Drop-off"
                      open={datePickerOpen.dropoff}
                      onClickOutside={() => {
                        if (
                          dateTimeComplete.dropoff ||
                          !rentalFormData.dropoffDate
                        ) {
                          setDatePickerOpen((prev) => ({
                            ...prev,
                            dropoff: false,
                          }));
                          if (dateTimeComplete.dropoff)
                            setTimeWarnings((prev) => ({
                              ...prev,
                              dropoff: false,
                            }));
                        } else {
                          setTimeWarnings((prev) => ({
                            ...prev,
                            dropoff: true,
                          }));
                        }
                      }}
                      wrapperClassName="w-full"
                      onInputClick={() =>
                        setDatePickerOpen((prev) => ({
                          ...prev,
                          dropoff: true,
                          pickup: false,
                        }))
                      }
                      renderDayContents={renderDayContents}
                      disabled={!selectedCar || !rentalFormData.pickupDate}
                      popperPlacement="bottom-start"
                      required
                      aria-required="true"
                      aria-describedby="dropoffDateError dropoffTimeWarning"
                      className={`w-full px-3 py-2 border ${
                        errors.dropoffDate
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar || !rentalFormData.pickupDate
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }`}
                    />
                    {errors.dropoffDate && (
                      <p
                        id="dropoffDateError"
                        className="text-red-600 text-xs mt-1"
                      >
                        {errors.dropoffDate}
                      </p>
                    )}
                    {timeWarnings.dropoff && !dateTimeComplete.dropoff && (
                      <p
                        id="dropoffTimeWarning"
                        className="text-yellow-600 text-xs mt-1"
                      >
                        Please select a time.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* --- Locations --- */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-700 text-base flex items-center">
                    Locations
                  </h4>
                  <label className="flex items-center text-xs sm:text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameLocation}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setSameLocation(isChecked);
                        if (isChecked) {
                          setRentalFormData((prev) => ({
                            ...prev,
                            dropoffLocation: prev.pickupLocation,
                            customDropoffLocation: prev.customPickupLocation,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            dropoffLocation: undefined,
                          }));
                        } else {
                          setRentalFormData((prev) => ({
                            ...prev,
                            dropoffLocation: "",
                            customDropoffLocation: "",
                          }));
                        }
                      }}
                      className="mr-1.5 sm:mr-2 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                      disabled={!selectedCar}
                    />
                    <span className="whitespace-nowrap">
                      Same return location
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pickup Location */}
                  <div>
                    <label
                      htmlFor="pickupLocation"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Pick-up Location *
                    </label>
                    <CustomSelect
                      id="pickupLocation"
                      value={rentalFormData.pickupLocation}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setRentalFormData((prev) => ({
                          ...prev,
                          pickupLocation: newValue,
                          customPickupLocation:
                            newValue !== "other"
                              ? ""
                              : prev.customPickupLocation,
                          ...(sameLocation
                            ? {
                                dropoffLocation: newValue,
                                customDropoffLocation:
                                  newValue !== "other"
                                    ? ""
                                    : prev.customPickupLocation,
                              }
                            : {}),
                        }));
                        if (newValue)
                          setErrors((prev) => ({
                            ...prev,
                            pickupLocation: undefined,
                          }));
                      }}
                      onOtherLocationChange={(value) => {
                        setRentalFormData((prev) => ({
                          ...prev,
                          customPickupLocation: value,
                          ...(sameLocation
                            ? { customDropoffLocation: value }
                            : {}),
                        }));
                        if (value.trim())
                          setErrors((prev) => ({
                            ...prev,
                            pickupLocation: undefined,
                          }));
                      }}
                      options={locationOptions}
                      placeholder="Select pick-up location"
                      showOtherInput={true}
                      initialCustomValue={rentalFormData.customPickupLocation}
                      disabled={!selectedCar}
                      required
                    />
                    {errors.pickupLocation && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.pickupLocation}
                      </p>
                    )}
                  </div>
                  {/* Dropoff Location (Conditional) */}
                  <div className={`${sameLocation ? "hidden" : "block"}`}>
                    <label
                      htmlFor="dropoffLocation"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Drop-off Location *
                    </label>
                    <CustomSelect
                      id="dropoffLocation"
                      value={rentalFormData.dropoffLocation}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setRentalFormData((prev) => ({
                          ...prev,
                          dropoffLocation: newValue,
                          customDropoffLocation:
                            newValue !== "other"
                              ? ""
                              : prev.customDropoffLocation,
                        }));
                        if (newValue)
                          setErrors((prev) => ({
                            ...prev,
                            dropoffLocation: undefined,
                          }));
                      }}
                      onOtherLocationChange={(value) => {
                        setRentalFormData((prev) => ({
                          ...prev,
                          customDropoffLocation: value,
                        }));
                        if (value.trim())
                          setErrors((prev) => ({
                            ...prev,
                            dropoffLocation: undefined,
                          }));
                      }}
                      options={locationOptions}
                      placeholder="Select drop-off location"
                      showOtherInput={true}
                      initialCustomValue={rentalFormData.customDropoffLocation}
                      disabled={!selectedCar || sameLocation}
                      required={!sameLocation}
                    />
                    {errors.dropoffLocation && !sameLocation && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.dropoffLocation}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* --- Customer Information --- */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-medium text-gray-700 mb-3 text-base">
                  Customer Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      First Name *
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      placeholder="Enter first name"
                      value={rentalFormData.firstName}
                      onChange={(e) => {
                        setRentalFormData({
                          ...rentalFormData,
                          firstName: e.target.value,
                        });
                        if (e.target.value.trim())
                          setErrors((prev) => ({
                            ...prev,
                            firstName: undefined,
                          }));
                      }}
                      disabled={!selectedCar}
                      required
                      className={`w-full px-3 py-2 border ${
                        errors.firstName
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Last Name *
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      placeholder="Enter last name"
                      value={rentalFormData.lastName}
                      onChange={(e) => {
                        setRentalFormData({
                          ...rentalFormData,
                          lastName: e.target.value,
                        });
                        if (e.target.value.trim())
                          setErrors((prev) => ({
                            ...prev,
                            lastName: undefined,
                          }));
                      }}
                      disabled={!selectedCar}
                      required
                      className={`w-full px-3 py-2 border ${
                        errors.lastName
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter email address"
                      value={rentalFormData.email}
                      onChange={(e) => {
                        setRentalFormData({
                          ...rentalFormData,
                          email: e.target.value,
                        });
                        if (/\S+@\S+\.\S+/.test(e.target.value))
                          setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      disabled={!selectedCar}
                      required
                      className={`w-full px-3 py-2 border ${
                        errors.email
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="Enter phone number"
                      value={rentalFormData.phone}
                      onChange={(e) => {
                        setRentalFormData({
                          ...rentalFormData,
                          phone: e.target.value,
                        });
                        if (e.target.value.trim())
                          setErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      disabled={!selectedCar}
                      required
                      className={`w-full px-3 py-2 border ${
                        errors.phone
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-md transition-colors ${
                        !selectedCar
                          ? "bg-gray-100 cursor-not-allowed opacity-70"
                          : ""
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  {/* Age */}
                  <div>
                    <label
                      htmlFor="age"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Age *
                    </label>
                    <select
                      id="age"
                      value={rentalFormData.age}
                      onChange={(e) => {
                        setRentalFormData({
                          ...rentalFormData,
                          age: e.target.value,
                        });
                        if (e.target.value)
                          setErrors((prev) => ({ ...prev, age: undefined }));
                      }}
                      disabled={!selectedCar}
                      required
                      className={`w-full h-10 px-3 border ${
                        errors.age ? "border-red-500" : "border-gray-300"
                      } rounded bg-white appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !selectedCar
                          ? "bg-gray-100 cursor-not-allowed opacity-70"
                          : ""
                      }`}
                    >
                      <option value="" disabled>
                        Select age
                      </option>
                      {Array.from({ length: 9 }, (_, i) => 18 + i).map(
                        (age) => (
                          <option key={age} value={age}>
                            {age} years old
                          </option>
                        )
                      )}
                      <option value="26+">26+ years old</option>
                    </select>
                    {errors.age && (
                      <p className="text-red-600 text-xs mt-1">{errors.age}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* --- Payment Method Section --- */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-medium text-gray-700 mb-3 text-base flex items-center">
                  Payment Method
                </h4>

                {loadingPaymentMethods ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-500">
                      Loading payment methods...
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paymentMethods.length > 0 ? (
                      paymentMethods
                        .filter((method) => method.status)
                        .map((method) => {
                          const getPaymentIcon = (title) => {
                            switch (title) {
                              case "Cash":
                                return "💰";
                              case "Credit Card":
                                return "💳";
                              case "Bank Deposit / Transfer":
                                return "🏦";
                              case "PayPal":
                                return "🅿️";
                              case "Check":
                                return "📋";
                              case "Other":
                                return "💼";
                              default:
                                return "💼";
                            }
                          };

                          return (
                            <label
                              key={method.id}
                              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                                rentalFormData.paymentMethod === method.id
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="radio"
                                name="paymentMethod"
                                value={method.id}
                                checked={
                                  rentalFormData.paymentMethod === method.id
                                }
                                onChange={(e) => {
                                  setRentalFormData({
                                    ...rentalFormData,
                                    paymentMethod: e.target.value,
                                  });
                                  setErrors((prev) => ({
                                    ...prev,
                                    paymentMethod: undefined,
                                  }));
                                }}
                                className="hidden"
                                disabled={!selectedCar}
                              />
                              <div
                                className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 transition-all duration-200 ${
                                  rentalFormData.paymentMethod === method.id
                                    ? "border-blue-500 bg-blue-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {rentalFormData.paymentMethod === method.id && (
                                  <div className="w-full h-full rounded-full bg-white scale-50" />
                                )}
                              </div>
                              <div className="flex-1">
                                <span className="font-medium text-gray-800 text-sm">
                                  {method.title_en}
                                </span>
                                {method.title_gr &&
                                  method.title_gr !== method.title_en && (
                                    <span className="text-xs text-gray-500 ml-1">
                                      ({method.title_gr})
                                    </span>
                                  )}
                              </div>
                              <span className="text-lg ml-2">
                                {getPaymentIcon(method.title_en)}
                              </span>
                            </label>
                          );
                        })
                    ) : (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-700 text-sm">
                          Payment methods are being loaded. Please wait...
                        </p>
                      </div>
                    )}

                    {errors.paymentMethod && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        data-error="true"
                      >
                        {errors.paymentMethod}
                      </p>
                    )}
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <p className="text-blue-700 text-xs">
                    <strong>Note:</strong> Payment will be processed upon
                    vehicle pickup. We accept cash, major credit cards, and bank
                    transfers.
                  </p>
                </div>
              </div>

              {/* --- Additional Options & Coupon --- */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-medium text-gray-700 mb-3 text-base">
                  Additional Options & Discounts
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Options */}
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Additional Options:
                    </p>
                    <div className="space-y-1">
                      {loadingAdditionalOptions ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-sm text-gray-500">
                            Loading options...
                          </span>
                        </div>
                      ) : additionalOptionsData.length > 0 ? (
                        additionalOptionsData.map((option) => (
                          <div key={option._id} className="relative group">
                            <CustomCheckbox
                              id={`option-${option._id}`}
                              checked={
                                rentalFormData.additionalOptions[option._id] ||
                                false
                              }
                              onChange={(e) =>
                                setRentalFormData((prev) => ({
                                  ...prev,
                                  additionalOptions: {
                                    ...prev.additionalOptions,
                                    [option._id]: e.target.checked,
                                  },
                                }))
                              }
                              label={`${option.name}${
                                option.price > 0
                                  ? ` (€${option.price.toFixed(2)}/day)`
                                  : " (Free)"
                              }`}
                            />
                            {/* Tooltip for description */}
                            <div className="absolute left-0 bottom-full mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                              {option.description}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 text-sm italic">
                          No additional options available
                        </div>
                      )}
                    </div>

                    {/* Advanced Payment Section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label
                        htmlFor="advancePayment"
                        className="block text-sm font-medium text-gray-600 mb-1"
                      >
                        Advanced Payment (€)
                      </label>
                      <div className="relative">
                        <input
                          id="advancePayment"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Amount already paid"
                          value={rentalFormData.advancePayment}
                          onChange={(e) => {
                            const value = e.target.value;
                            setRentalFormData((prev) => ({
                              ...prev,
                              advancePayment: value,
                            }));

                            if (!value) {
                              setErrors((prev) => ({
                                ...prev,
                                advancePayment: undefined,
                              }));
                            }
                          }}
                          onBlur={() => {
                            if (rentalFormData.advancePayment) {
                              const advancePayment = parseFloat(
                                rentalFormData.advancePayment
                              );
                              if (isNaN(advancePayment) || advancePayment < 0) {
                                setErrors((prev) => ({
                                  ...prev,
                                  advancePayment: "Must be a positive number",
                                }));
                              } else if (advancePayment > totalPrice) {
                                setErrors((prev) => ({
                                  ...prev,
                                  advancePayment: "Cannot exceed total price",
                                }));
                              } else {
                                setErrors((prev) => ({
                                  ...prev,
                                  advancePayment: undefined,
                                }));
                              }
                            }
                          }}
                          disabled={
                            !selectedCar ||
                            !rentalFormData.pickupDate ||
                            !rentalFormData.dropoffDate ||
                            totalPrice <= 0
                          }
                          className={`w-full px-3 py-2 border ${
                            errors.advancePayment
                              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          } rounded-md transition-colors ${
                            !selectedCar ||
                            !rentalFormData.pickupDate ||
                            !rentalFormData.dropoffDate ||
                            totalPrice <= 0
                              ? "bg-gray-100 cursor-not-allowed opacity-70"
                              : ""
                          }`}
                        />
                        {errors.advancePayment && (
                          <p className="text-red-600 text-xs mt-1">
                            {errors.advancePayment}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {totalPrice > 0
                            ? `Enter amount already paid (max: €${totalPrice.toFixed(
                                2
                              )})`
                            : "Select car and dates to enable"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Coupon */}
                  <div>
                    <label
                      htmlFor="couponCode"
                      className="block text-sm font-medium text-gray-600 mb-1"
                    >
                      Coupon Code
                    </label>
                    <div className="relative">
                      <input
                        id="couponCode"
                        type="text"
                        placeholder="Enter coupon code"
                        value={rentalFormData.couponCode}
                        onChange={(e) => {
                          const code = e.target.value.toUpperCase();
                          setRentalFormData({
                            ...rentalFormData,
                            couponCode: code,
                          });
                          setCouponStatus((prev) => ({ ...prev, message: "" }));
                        }}
                        onBlur={() => validateCoupon(rentalFormData.couponCode)}
                        disabled={
                          !selectedCar ||
                          !rentalFormData.pickupDate ||
                          !rentalFormData.dropoffDate
                        }
                        className={`w-full px-3 py-2 border rounded-md transition-colors ${
                          couponStatus.isValid
                            ? "border-green-500 focus:ring-green-500 focus:border-green-500"
                            : couponStatus.message
                            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-500"
                        } ${
                          !selectedCar ||
                          !rentalFormData.pickupDate ||
                          !rentalFormData.dropoffDate
                            ? "bg-gray-100 cursor-not-allowed opacity-70"
                            : ""
                        }`}
                      />
                      {couponStatus.message && (
                        <div
                          className={`text-xs mt-1 overflow-hidden ${
                            couponStatus.isValid
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {couponStatus.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Notes */}
                <div className="mt-4">
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-600 mb-1"
                  >
                    Additional Notes:
                  </label>
                  <textarea
                    id="notes"
                    value={rentalFormData.notes}
                    onChange={(e) =>
                      setRentalFormData({
                        ...rentalFormData,
                        notes: e.target.value,
                      })
                    }
                    rows="3"
                    disabled={!selectedCar}
                    className={`w-full p-3 border border-gray-300 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !selectedCar
                        ? "bg-gray-100 cursor-not-allowed opacity-70"
                        : ""
                    }`}
                    placeholder="e.g., Flight number, specific delivery instructions..."
                  />
                </div>
              </div>

                                      {/* --- Invoice Section (Admin) --- */}
                                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                        <h4 className="font-medium text-gray-700 mb-3 text-base">Invoice Information</h4>
                                        
                                        {/* Invoice Toggle */}
                                        <div className="mb-4">
                                          <CustomCheckbox
                                            id="wantsInvoice"
                                            checked={wantsInvoice}
                                            onChange={(e) => {
                                              setWantsInvoice(e.target.checked);
                                              if (!e.target.checked) {
                                                // Clear invoice errors when unchecked
                                                setErrors(prev => {
                                                  const newErrors = { ...prev };
                                                  delete newErrors.companyName;
                                                  delete newErrors.vatNumber;
                                                  delete newErrors.address;
                                                  delete newErrors.city;
                                                  delete newErrors.postalCode;
                                                  return newErrors;
                                                });
                                              }
                                            }}
                                            label="Customer wants invoice"
                                            disabled={!selectedCar}
                                          />
                                        </div>
              
                                        {/* Invoice Form Fields */}
                                        <AnimatePresence>
                                          {wantsInvoice && (
                                            <motion.div
                                              initial={{ opacity: 0, height: 0 }}
                                              animate={{ opacity: 1, height: "auto" }}
                                              exit={{ opacity: 0, height: 0 }}
                                              transition={{ duration: 0.3 }}
                                              className="overflow-hidden"
                                            >
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                                                {/* Company Name */}
                                                <div>
                                                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-600 mb-1">
                                                    Company Name *
                                                  </label>
                                                  <input
                                                    id="companyName"
                                                    type="text"
                                                    placeholder="Enter company name"
                                                    value={invoiceData.companyName}
                                                    onChange={(e) => {
                                                      setInvoiceData(prev => ({ ...prev, companyName: e.target.value }));
                                                      if (e.target.value.trim()) {
                                                        setErrors(prev => ({ ...prev, companyName: undefined }));
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${
                                                      errors.companyName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md transition-colors`}
                                                    data-error={!!errors.companyName}
                                                  />
                                                  {errors.companyName && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.companyName}</p>
                                                  )}
                                                </div>
              
                                                {/* VAT Number */}
                                                <div>
                                                  <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-600 mb-1">
                                                    VAT Number *
                                                  </label>
                                                  <input
                                                    id="vatNumber"
                                                    type="text"
                                                    placeholder="Enter VAT number"
                                                    value={invoiceData.vatNumber}
                                                    onChange={(e) => {
                                                      setInvoiceData(prev => ({ ...prev, vatNumber: e.target.value }));
                                                      if (e.target.value.trim()) {
                                                        setErrors(prev => ({ ...prev, vatNumber: undefined }));
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${
                                                      errors.vatNumber ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md transition-colors`}
                                                    data-error={!!errors.vatNumber}
                                                  />
                                                  {errors.vatNumber && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.vatNumber}</p>
                                                  )}
                                                </div>
              
                                                {/* Address */}
                                                <div className="md:col-span-2">
                                                  <label htmlFor="invoiceAddress" className="block text-sm font-medium text-gray-600 mb-1">
                                                    Address *
                                                  </label>
                                                  <input
                                                    id="invoiceAddress"
                                                    type="text"
                                                    placeholder="Enter company address"
                                                    value={invoiceData.address}
                                                    onChange={(e) => {
                                                      setInvoiceData(prev => ({ ...prev, address: e.target.value }));
                                                      if (e.target.value.trim()) {
                                                        setErrors(prev => ({ ...prev, address: undefined }));
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${
                                                      errors.address ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md transition-colors`}
                                                    data-error={!!errors.address}
                                                  />
                                                  {errors.address && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.address}</p>
                                                  )}
                                                </div>
              
                                                {/* City */}
                                                <div>
                                                  <label htmlFor="invoiceCity" className="block text-sm font-medium text-gray-600 mb-1">
                                                    City *
                                                  </label>
                                                  <input
                                                    id="invoiceCity"
                                                    type="text"
                                                    placeholder="Enter city"
                                                    value={invoiceData.city}
                                                    onChange={(e) => {
                                                      setInvoiceData(prev => ({ ...prev, city: e.target.value }));
                                                      if (e.target.value.trim()) {
                                                        setErrors(prev => ({ ...prev, city: undefined }));
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${
                                                      errors.city ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md transition-colors`}
                                                    data-error={!!errors.city}
                                                  />
                                                  {errors.city && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.city}</p>
                                                  )}
                                                </div>
              
                                                {/* Postal Code */}
                                                <div>
                                                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-600 mb-1">
                                                    Postal Code *
                                                  </label>
                                                  <input
                                                    id="postalCode"
                                                    type="text"
                                                    placeholder="Enter postal code"
                                                    value={invoiceData.postalCode}
                                                    onChange={(e) => {
                                                      setInvoiceData(prev => ({ ...prev, postalCode: e.target.value }));
                                                      if (e.target.value.trim()) {
                                                        setErrors(prev => ({ ...prev, postalCode: undefined }));
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${
                                                      errors.postalCode ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md transition-colors`}
                                                    data-error={!!errors.postalCode}
                                                  />
                                                  {errors.postalCode && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.postalCode}</p>
                                                  )}
                                                </div>
              
                                                {/* Country */}
                                                <div className="md:col-span-2">
                                                  <label htmlFor="country" className="block text-sm font-medium text-gray-600 mb-1">
                                                    Country
                                                  </label>
                                                  <input
                                                    id="country"
                                                    type="text"
                                                    placeholder="Enter country"
                                                    value={invoiceData.country}
                                                    onChange={(e) => setInvoiceData(prev => ({ ...prev, country: e.target.value }))
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md transition-colors focus:ring-blue-500 focus:border-blue-500"
                                                  />
                                                </div>
                                              </div>
                                            </motion.div>
                                          )}
                                        </AnimatePresence>
                                      </div>

              {/* --- Price Breakdown --- */}
              <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-4 rounded-lg shadow-lg text-white">
                <h4 className="font-medium text-white mb-4 flex items-center">
                  Price Breakdown
                </h4>

                {/* Custom Price Input Section */}
                <div className="mb-4 border-b border-blue-600 pb-3">
                  <div className="flex justify-between items-center mb-1">
                    <label
                      htmlFor="customPrice"
                      className="block text-sm text-gray-200"
                    >
                      Custom Price Per Day (Admin Only)
                    </label>
                    {rentalFormData.customPricePerDay && (
                      <button
                        type="button"
                        onClick={() =>
                          setRentalFormData((prev) => ({
                            ...prev,
                            customPricePerDay: "",
                          }))
                        }
                        className="text-xs text-blue-300 hover:text-blue-100 underline focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1"
                      >
                        Clear Custom Price
                      </button>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-white text-lg mr-2">€</span>
                    <input
                      id="customPrice"
                      type="number"
                      value={rentalFormData.customPricePerDay}
                      onChange={(e) =>
                        setRentalFormData((prev) => ({
                          ...prev,
                          customPricePerDay: e.target.value,
                        }))
                      }
                      min="0"
                      step="0.01"
                      placeholder="Enter custom daily rate"
                      disabled={
                        !selectedCar ||
                        !rentalFormData.pickupDate ||
                        !rentalFormData.dropoffDate
                      }
                      className={`w-full bg-blue-800 border border-blue-600 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                        !selectedCar ||
                        !rentalFormData.pickupDate ||
                        !rentalFormData.dropoffDate
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                  <p className="text-xs text-blue-300 mt-1">
                    Overrides default/dynamic prices if set.
                  </p>
                </div>

                {/* Detailed Breakdown */}
                <div className="space-y-1 text-sm mb-3">
                  {rentalFormData.pickupDate &&
                  rentalFormData.dropoffDate &&
                  selectedCar ? (
                    calculatePriceBreakdown()?.length > 0 ? (
                      calculatePriceBreakdown().map((period, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <span className="text-gray-300">
                            {period.days > 1
                              ? `${formatDateRange(
                                  period.startDate
                                )} - ${formatDateRange(period.endDate)}`
                              : formatDateRange(period.startDate)}
                            {` (${period.days} day${
                              period.days > 1 ? "s" : ""
                            })`}
                          </span>
                          <span
                            className={`font-medium ${
                              rentalFormData.customPricePerDay &&
                              parseFloat(rentalFormData.customPricePerDay) >
                                0 &&
                              !isNaN(
                                parseFloat(rentalFormData.customPricePerDay)
                              )
                                ? "text-yellow-300"
                                : period.price !== selectedCar.basePrice
                                ? "text-blue-300"
                                : "text-gray-100"
                            }`}
                          >
                            €{period.price.toFixed(2)}/day
                            {rentalFormData.customPricePerDay &&
                              parseFloat(rentalFormData.customPricePerDay) >
                                0 &&
                              !isNaN(
                                parseFloat(rentalFormData.customPricePerDay)
                              ) &&
                              index === 0 && (
                                <span className="text-xs ml-1">(Custom)</span>
                              )}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-blue-200 py-2">
                        Calculating price...
                      </p>
                    )
                  ) : (
                    <p className="text-center text-blue-200 py-2">
                      Select car and dates.
                    </p>
                  )}
                </div>

                {/* Totals Section */}
                {rentalFormData.pickupDate &&
                  rentalFormData.dropoffDate &&
                  selectedCar &&
                  calculatePriceBreakdown()?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-blue-600 space-y-1.5 text-sm">
                      {/* Duration Discount */}
                      {(() => {
                        const diffTime = Math.abs(
                          new Date(rentalFormData.dropoffDate) -
                            new Date(rentalFormData.pickupDate)
                        );
                        const days = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        );
                        let discountPercentage = 0;
                        if (days > 10 && durationDiscounts.longTerm?.discount)
                          discountPercentage =
                            durationDiscounts.longTerm.discount;
                        else if (
                          days > 3 &&
                          durationDiscounts.mediumTerm?.discount
                        )
                          discountPercentage =
                            durationDiscounts.mediumTerm.discount;
                        else if (durationDiscounts.shortTerm?.discount)
                          discountPercentage =
                            durationDiscounts.shortTerm.discount;

                        if (discountPercentage > 0) {
                          const subtotalBeforeDiscounts =
                            calculatePriceBreakdown()?.reduce(
                              (total, period) =>
                                total + period.price * period.days,
                              0
                            ) || 0;
                          const durationDiscountAmount =
                            (subtotalBeforeDiscounts * discountPercentage) /
                            100;
                          return (
                            <div className="flex justify-between items-center text-green-300">
                              <span>
                                Duration Discount ({discountPercentage}%):
                              </span>
                              <span>-€{durationDiscountAmount.toFixed(2)}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {/* Subtotal */}
                      <div className="flex justify-between items-center text-gray-200">
                        <span>Subtotal (before VAT):</span>
                        <span>
                          €
                          {(
                            totalPrice / 1.24 +
                            (couponStatus.isValid ? couponStatus.discount : 0)
                          ).toFixed(2)}
                        </span>
                      </div>
                      {/* Coupon Discount */}
                      {couponStatus.isValid && (
                        <div className="flex justify-between items-center text-green-300">
                          <span>Coupon Discount:</span>
                          <span>-€{couponStatus.discount.toFixed(2)}</span>
                        </div>
                      )}
                      {/* Net Total */}
                      <div className="flex justify-between items-center text-gray-100 font-medium">
                        <span>Net Total (before VAT):</span>
                        <span>€{(totalPrice / 1.24).toFixed(2)}</span>
                      </div>
                      {/* VAT */}
                      <div className="flex justify-between items-center text-gray-200">
                        <span>VAT (24%):</span>
                        <span>
                          €{(totalPrice - totalPrice / 1.24).toFixed(2)}
                        </span>
                      </div>
                      {/* Grand Total */}
                      <div className="flex justify-between items-center font-bold text-lg mt-3 pt-3 border-t border-blue-600">
                        <span>Total:</span>
                        <span>€{totalPrice.toFixed(2)}</span>
                      </div>

                      {/* Advanced Payment Display */}
                      {rentalFormData.advancePayment &&
                        parseFloat(rentalFormData.advancePayment) > 0 && (
                          <>
                            <div className="flex justify-between items-center text-yellow-300 mt-2">
                              <span>Advanced Payment:</span>
                              <span>
                                -€
                                {parseFloat(
                                  rentalFormData.advancePayment
                                ).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center font-bold bg-blue-800 mt-2 p-2 rounded">
                              <span>Balance Due:</span>
                              <span>
                                €
                                {Math.max(
                                  0,
                                  totalPrice -
                                    parseFloat(rentalFormData.advancePayment)
                                ).toFixed(2)}
                              </span>
                            </div>
                          </>
                        )}
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // --- useEffect Hooks ---
  // Initialize data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    } else {
      resetForm();
    }
  }, [isOpen]);

  // Set pickup date when selectedDate is provided
  useEffect(() => {
    if (selectedDate && isOpen) {
      // Set the pickup date to the selected date at 9:00 AM
      const initialPickupDate = new Date(selectedDate);
      initialPickupDate.setHours(9, 0, 0, 0);

      // Set dropoff date to the next day at 9:00 AM by default
      const initialDropoffDate = new Date(selectedDate);
      initialDropoffDate.setDate(initialDropoffDate.getDate() + 1);
      initialDropoffDate.setHours(9, 0, 0, 0);

      setRentalFormData((prev) => ({
        ...prev,
        pickupDate: initialPickupDate,
        dropoffDate: initialDropoffDate,
      }));

      setDateTimeComplete({
        pickup: true,
        dropoff: true,
      });
    }
  }, [selectedDate, isOpen]);
  const fetchInitialData = async () => {
    setInitialLoading(true);
    try {
      await Promise.all([
        fetchCars(),
        fetchDurationDiscounts(),
        fetchAdditionalOptions(),
        fetchPaymentMethods(),
        fetchLocations(),
      ]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load initial data");
    } finally {
      setInitialLoading(false);
    }
  };
  const fetchCars = async () => {
    try {
      const response = await cars.getAll();
      // Filter for available cars
      setAvailableCars(
        response.data.filter((car) => car.status === "available")
      );
    } catch (error) {
      console.error("Error fetching cars:", error);
      setAvailableCars([]);
      setErrors((prev) => ({ ...prev, car: "Failed to load cars." }));
    }
  };

  const fetchDurationDiscounts = async () => {
    try {
      const response = await rentals.getDurationDiscounts();
      setDurationDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching duration discounts:", error);
      // Keep default discounts if fetch fails
    }
  };
  const fetchAdditionalOptions = async () => {
    setLoadingAdditionalOptions(true);
    try {
      const options = await additionalOptions.getActive();

      if (options && Array.isArray(options)) {
        setAdditionalOptionsData(options);

        // Initialize rentalFormData.additionalOptions with all options set to false
        const initializedOptions = {};
        options.forEach((option) => {
          initializedOptions[option._id] = false;
        });

        setRentalFormData((prev) => ({
          ...prev,
          additionalOptions: initializedOptions,
        }));
      } else {
        // Fallback to default options if API fails
        const defaultOptions = [
          {
            _id: "default-child-seat",
            name: "Child Seat",
            description: "Add a Child Seat to your Vehicle!",
            price: 0,
            isEnabled: true,
          },
          {
            _id: "default-baby-chair",
            name: "Baby Chair",
            description: "Add a Baby Chair to your Vehicle!",
            price: 0,
            isEnabled: true,
          },
          {
            _id: "default-gps",
            name: "GPS",
            description: "Navigation system for your journey",
            price: 0,
            isEnabled: true,
          },
        ];
        setAdditionalOptionsData(defaultOptions);

        const initializedOptions = {};
        defaultOptions.forEach((option) => {
          initializedOptions[option._id] = false;
        });

        setRentalFormData((prev) => ({
          ...prev,
          additionalOptions: initializedOptions,
        }));
      }
    } catch (error) {
      console.error("Error fetching additional options:", error);
      // Fallback to default options
      const defaultOptions = [
        {
          _id: "default-child-seat",
          name: "Child Seat",
          description: "Add a Child Seat to your Vehicle!",
          price: 0,
          isEnabled: true,
        },
        {
          _id: "default-baby-chair",
          name: "Baby Chair",
          description: "Add a Baby Chair to your Vehicle!",
          price: 0,
          isEnabled: true,
        },
        {
          _id: "default-gps",
          name: "GPS",
          description: "Navigation system for your journey",
          price: 0,
          isEnabled: true,
        },
      ];
      setAdditionalOptionsData(defaultOptions);

      const initializedOptions = {};
      defaultOptions.forEach((option) => {
        initializedOptions[option._id] = false;
      });

      setRentalFormData((prev) => ({
        ...prev,
        additionalOptions: initializedOptions,
      }));
    } finally {
      setLoadingAdditionalOptions(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locations.getActive();

      if (response && Array.isArray(response)) {
        // Map API locations to the format expected by the component
        const apiLocations = response.map((location) => ({
          value: location._id,
          label: location.name,
        }));

        // Always add "other" option
        apiLocations.push({
          value: "other",
          label: "Other Location (Hotel/Restaurant/Beach)",
        });

        setLocationOptions(apiLocations);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      // Keep using the default locations if API fails
    }
  };

  // Delete created rental function
  const deleteCreatedRental = async () => {
    if (!createdRental?._id) return;

    try {
      setLoading(true);
      await rentals.delete(createdRental._id);
      console.log('Successfully deleted rental:', createdRental._id);
      setCreatedRental(null);
    } catch (error) {
      console.error('Error deleting created rental:', error);
      // Still reset the state even if deletion fails to prevent duplicates
      setCreatedRental(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToRental = async () => {
  if (currentStep === 2 && createdRental) {
    await deleteCreatedRental();
  }
  setCurrentStep(1);
  setHandoverCurrentStep(1);
  setHandoverStepErrors({
    step1: null,
    step2: null,
    step3: null,
    step4: null,
  });
};

const handleModalClose = async () => {
    Swal.fire({
  title: "Confirm",
  text: "Are you sure you want to close the modal? Any rentals created or unsaved changes will be lost.",
  icon: "warning",
  showCancelButton: true,
  confirmButtonColor: "#3085d6",
  cancelButtonColor: "#d33",
  confirmButtonText: "Yes, close it",
}).then(async (result) => {
  if (result.isConfirmed) { 
  // If there is a created rental, delete it
  if (createdRental) {
    await deleteCreatedRental();
  }
  // Reset all form data and states
  setRentalFormData({
    pickupDate: null,
    dropoffDate: null,
    pickupLocation: "",
    dropoffLocation: "",
    customPickupLocation: "",
    customDropoffLocation: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "",
    paymentMethod: "",
    advancePayment: "",
    additionalOptions: {},
    car: null,
  });
  setSelectedCar(null);
  setErrors({});
  setCreatedRental(null);
  setCurrentStep(1);
  setHandoverCurrentStep(1);
  setHandoverStepErrors({
    step1: null,
    step2: null,
    step3: null,
    step4: null,
  });
  onClose();
  }
});
};

  const validateForm = () => {
    const newErrors = {};

    // Car Selection
    if (!selectedCar) newErrors.car = "Please select a car";

    // Dates & Times
    if (!rentalFormData.pickupDate)
      newErrors.pickupDate = "Pick-up date & time required";
    else if (!dateTimeComplete.pickup)
      newErrors.pickupDate = "Pick-up time required";

    if (!rentalFormData.dropoffDate)
      newErrors.dropoffDate = "Drop-off date & time required";
    else if (!dateTimeComplete.dropoff)
      newErrors.dropoffDate = "Drop-off time required";

    // Date Order Validation
    if (
      rentalFormData.pickupDate &&
      rentalFormData.dropoffDate &&
      rentalFormData.pickupDate >= rentalFormData.dropoffDate
    ) {
      newErrors.dropoffDate = "Drop-off must be strictly after pick-up";
    }

    // Advanced Payment Validation
    if (rentalFormData.advancePayment) {
      const advancePayment = parseFloat(rentalFormData.advancePayment);
      if (isNaN(advancePayment) || advancePayment < 0) {
        newErrors.advancePayment = "Advanced payment must be a positive number";
      } else if (advancePayment > totalPrice) {
        newErrors.advancePayment = "Advanced payment cannot exceed total price";
      }
    }

    // Customer Info
    if (!rentalFormData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!rentalFormData.lastName.trim())
      newErrors.lastName = "Last name is required";
    if (!rentalFormData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(rentalFormData.email)) {
      newErrors.email = "Invalid email address format";
    }
    if (!rentalFormData.phone.trim())
      newErrors.phone = "Phone number is required";
    else if (!/^\+?[0-9\s\-()]{7,}$/.test(rentalFormData.phone)) {
      newErrors.phone = "Invalid phone number format";
    }
    if (!rentalFormData.age) newErrors.age = "Age selection is required";

    // Locations
    if (!rentalFormData.pickupLocation) {
      newErrors.pickupLocation = "Pick-up location is required";
    } else if (
      rentalFormData.pickupLocation === "other" &&
      !rentalFormData.customPickupLocation.trim()
    ) {
      newErrors.pickupLocation = "Please specify the custom pick-up location";
    }

    if (!sameLocation) {
      if (!rentalFormData.dropoffLocation) {
        newErrors.dropoffLocation = "Drop-off location is required";
      } else if (
        rentalFormData.dropoffLocation === "other" &&
        !rentalFormData.customDropoffLocation.trim()
      ) {
        newErrors.dropoffLocation =
          "Please specify the custom drop-off location";
      }
    }

        // Invoice Validation (NEW)
    if (wantsInvoice) {
      if (!invoiceData.companyName.trim()) newErrors.companyName = "Company name is required for invoice";
      if (!invoiceData.vatNumber.trim()) newErrors.vatNumber = "VAT number is required for invoice";
      if (!invoiceData.address.trim()) newErrors.address = "Address is required for invoice";
      if (!invoiceData.city.trim()) newErrors.city = "City is required for invoice";
      if (!invoiceData.postalCode.trim()) newErrors.postalCode = "Postal code is required for invoice";
    } 

    // Payment method validation
    if (!rentalFormData.paymentMethod) {
      newErrors.paymentMethod = "Please select a payment method";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- Invoice Generation Colors ---
  const colors = {
    primary: "#D81919", // Example primary color
    secondary: "#990000", // Example secondary color
    accent: "#FFEDED", // Example accent color
    dark: "#333333",
    light: "#FFFFFF",
    gray: "#F8F8F8",
  };

  const generateInvoice = async (rental) => {
    const invoiceDiv = document.createElement("div");
    invoiceDiv.style.padding = "10px";
    invoiceDiv.style.position = "absolute";
    invoiceDiv.style.left = "-9999px";
    invoiceDiv.style.background = "white";
    invoiceDiv.style.width = "900px";

    const header = `
    <div style="display: flex; justify-content: space-between; margin-bottom: 40px; position: relative;">
      <div style="width: 66%; position: relative; overflow: hidden; height: 120px;">
        <div style="position: absolute; top: -20px; left: -200px; width: calc(100% + 220px); height: calc(100% + 40px); transform: skew(-15deg); z-index: 0; box-shadow: 0 3px 10px rgba(0,0,0,0.2);"></div>
        <img src="${logo}" alt="Logo" style="width: 85%; height: 100%; object-fit: contain; position: relative; z-index: 1; padding: 20px 20px 20px 40px;" />
      </div>
      <div style="width: 32%; text-align: right; padding: 20px; background: ${
        colors.gray
      }; border-right: 5px solid ${
      colors.primary
    }; box-shadow: 0 3px 8px rgba(0,0,0,0.1);">
        <h2 style="margin-bottom: 15px; color: ${
          colors.primary
        }; font-size: 22px; font-weight: bold;">Contact Information</h2>
        <p style="margin: 5px 0; color: ${
          colors.dark
        };">Epar. Odos Mykonos 846 00</p>
        <p style="margin: 5px 0; color: ${colors.dark};">nleont@gmail.com</p>
        <p style="margin: 5px 0; color: ${
          colors.dark
        }; font-weight: bold;">+306941419012</p>
        <p style="margin: 5px 0; color: ${colors.primary};">${
      window.location.origin
    }</p>
      </div>
    </div>
    <div style="text-align: center; margin-bottom: 30px; padding: 10px 0; border-bottom: 2px solid ${
      colors.primary
    }; border-top: 2px solid ${colors.primary};">
      <h1 style="margin: 0; color: ${
        colors.primary
      }; font-size: 28px; text-transform: uppercase; letter-spacing: 2px;">PRE-INVOICE</h1>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Pre-Invoice #: INV-${new Date().getFullYear()}-${Math.floor(
      Math.random() * 1000000
    )}</p>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Pre-Invoice Date: ${new Date().toLocaleDateString("en-GB")}</p>
    </div>
  `;

    console.log("Generating invoice for rental:", rental);

    const infoSection = `
    <div style="display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px;">
      <div style="flex: 1; background: ${
        colors.accent
      }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
      colors.primary
    }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
      colors.primary
    }; padding-bottom: 8px;">CLIENT DETAILS</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;">${
        rental.customer.firstName
      } ${rental.customer.lastName}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Phone:</span> ${rental.customer.phoneNumber}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Email:</span> ${rental.customer.email}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Age:</span> ${rental.customer.age}</p>
      </div>
      <div style="flex: 1; background: ${
        colors.accent
      }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
      colors.primary
    }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
      colors.primary
    }; padding-bottom: 8px;">VEHICLE INFORMATION</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Vehicle Name:</span> ${
      rental.car
        ? rental.car.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : rental.carName
    }</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Rental Period:</span> ${new Date(
      rental.pickupDateTime
    ).toLocaleDateString("en-GB")} ${new Date(
      rental.pickupDateTime
    ).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })} - ${new Date(rental.returnDateTime).toLocaleDateString(
      "en-GB"
    )} ${new Date(rental.returnDateTime).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Pick-up:</span> ${rental.pickupLocation}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Return:</span> ${rental.returnLocation}</p>
      </div>
    </div>
    `;

    const priceRows = rental.priceBreakdown
      .map(
        (period, index) => `
    <tr style="background-color: ${
      index % 2 === 0 ? colors.light : colors.accent
    };">
      <td style="padding: 14px; border-bottom: 1px solid #ddd; font-weight: bold;">
        Rental (${new Date(period.startDate).toLocaleDateString(
          "en-GB"
        )} - ${new Date(period.endDate).toLocaleDateString("en-GB")})
      </td>
      <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">€${
        period.pricePerDay
      }</td>
      <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">${
        period.numberOfDays
      }</td>
      <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">€${
        period.totalForPeriod
      }</td>
    </tr>
  `
      )
      .join("");

    // Generate additional options rows matching CarDetails format
    const additionalOptionsRows = [];
    if (
      rental.additionalOptions &&
      Array.isArray(rental.additionalOptions) &&
      rental.totalRentalDays !== undefined
    ) {
      const totalDaysForInvoice = rental.totalRentalDays;
      rental.additionalOptions.forEach((option) => {
        additionalOptionsRows.push(
          `<tr style="background-color: ${colors.accent};">
             <td style="padding: 14px; border-bottom: 1px solid #ddd;">${
               option.name
             } (Additional Option)</td>
             <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">€${option.price.toFixed(
               2
             )}</td>
             <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">${totalDaysForInvoice}</td>
             <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">€${(
               option.price * totalDaysForInvoice
             ).toFixed(2)}</td>
           </tr>`
        );
      });
    }

    const priceTable = `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; border: 2px solid ${
      colors.primary
    }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <thead>
        <tr style="background: ${colors.primary}; color: white;">
          <th style="padding: 15px; text-align: left; font-size: 15px;">Description</th>
          <th style="padding: 15px; text-align: center; font-size: 15px;">Rate</th>
          <th style="padding: 15px; text-align: center; font-size: 15px;">Quantity</th>
          <th style="padding: 15px; text-align: right; font-size: 15px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${priceRows}
        ${additionalOptionsRows.join("")}
      </tbody>
    </table>
  `;

    const additionalNotesSection = rental.additionalNotes
      ? `
      <div style="margin-bottom: 20px; background: #f8f8f8; padding: 15px; border-left: 4px solid #1e3a8a;">
        <h4 style="margin-bottom: 10px; color: #1e3a8a; text-transform: uppercase; font-weight: bold;">Additional Notes</h4>
        <p style="margin: 0; font-style: italic;">${rental.additionalNotes}</p>
      </div>
    `
      : "";

    const totalsSection = `
    <div style="display: flex; justify-content: flex-end;">
      <div style="width: 350px;">
        <div style="border: 2px solid ${
          colors.primary
        }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          };">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">Subtotal:</span>
              <span>€${(rental.totalPrice / 1.24).toFixed(2)}</span>
            </div>
          </div>
          ${
            rental.discountAmount
              ? `
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          }; color: ${colors.primary};">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">Discount:</span>
              <span>-€${rental.discountAmount.toFixed(2)}</span>
            </div>
          </div>
          `
              : ""
          }
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          };">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">VAT (24%):</span>
              <span>€${(rental.totalPrice - rental.totalPrice / 1.24).toFixed(
                2
              )}</span>
            </div>
          </div>
          <div style="padding: 16px; background: ${
            colors.primary
          }; color: white;">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 18px;">
              <span>TOTAL:</span>
              <span>€${rental.totalPrice.toFixed(2)} (NOT PAID)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

    const footer = `
    <div style="margin-top: 50px;">
      <div style="margin-bottom: 30px; padding: 20px; background: ${colors.accent}; border-radius: 8px; border-left: 6px solid ${colors.primary}; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
        <p style="margin: 0; font-style: italic; color: ${colors.dark};">Thank you for choosing <span style="font-weight: bold; color: ${colors.primary};">Luxury Car Mykonos Rental</span>. For assistance, contact us at <span style="font-weight: bold;">+306941419012</span> or <span style="font-weight: bold;">nleont@gmail.com</span></p>
      </div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 30px;">
        <div style="width: 30%;">
          <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
          <div style="background: ${colors.primary}; height: 2px;"></div>
        </div>
        <div style="width: 200px; text-align: center;">
          <div style="border-bottom: 2px solid ${colors.primary}; margin-bottom: 8px; padding-bottom: 15px;">
            <img src="data:image/png;base64,..." alt="Signature" style="max-width: 150px;" />
          </div>
          <p style="margin: 0; font-weight: bold; color: ${colors.primary};">Nikos Leontitsis</p>
          <p style="margin: 0; color: ${colors.dark};">Managing Director</p>
        </div>
        <div style="width: 30%;">
          <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
          <div style="background: ${colors.primary}; height: 2px;"></div>
        </div>
      </div>
    </div>
    <div style="margin-top: 40px; text-align: center; padding-top: 20px; border-top: 2px dashed ${colors.accent};">
    </div>
  `;

    invoiceDiv.innerHTML =
      header +
      infoSection +
      priceTable +
      additionalNotesSection +
      totalsSection +
      footer;
    document.body.appendChild(invoiceDiv);

    try {
      const scale = 2;
      const canvas = await html2canvas(invoiceDiv, {
        scale: scale,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width / scale;
      const imgHeight = canvas.height / scale;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 20;

      pdf.addImage(
        imgData,
        "JPEG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
      }

      const pdfData = pdf.output("datauristring");
      return pdfData.split(",")[1];
    } finally {
      document.body.removeChild(invoiceDiv);
    }
  };

  const getLocationNameFromId = (locationId) => {
    console.log("getLocationNameFromId called with:", locationId);
    console.log("Available locationOptions:", locationOptions);

    // Handle empty or null values
    if (!locationId) {
      console.log("No locationId provided, returning Unknown Location");
      return "Unknown Location";
    }

    // Handle "other" location type
    if (locationId === "other") {
      console.log('LocationId is "other", returning Other Location');
      return "Other Location";
    }

    // Handle custom location inputs
    if (
      locationId === rentalFormData.customPickupLocation ||
      locationId === rentalFormData.customDropoffLocation
    ) {
      console.log("LocationId matches custom location:", locationId);
      return locationId; // Return the custom location text
    }

    // Try to find the location option with the matching ID
    const locationOption = locationOptions.find(
      (option) => option.value === locationId
    );
    console.log("Found location option:", locationOption);

    if (locationOption) {
      console.log("Returning location label:", locationOption.label);
      return locationOption.label;
    } else {
      console.log(
        "No matching location found, returning original ID:",
        locationId
      );
      return locationId;
    }
  };

  // Rental form functions (from CreateRentalModal)
  const handleRentalInputChange = (e) => {
    const { name, value } = e.target;
    setRentalFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCarSelect = (car) => {
    setSelectedCar(car);
    console.log("Selected car:", car);
    setRentalFormData((prev) => ({ ...prev, car: car, carId: car._id }));
    fetchInitialPricing(car.category, car.stringId);
    // Clear car selection error if present
    setErrors((prev) => ({ ...prev, car: undefined }));
  };

  // Fetch Initial/Dynamic Pricing for Selected Car
  const fetchInitialPricing = async (category, stringId) => {
    try {
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);

      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        category: category,
        includeAll: true,
      });

      const carAvailability =
        response.data.find((c) => c.stringId === stringId)?.availability || [];
      setAvailabilityData({ dynamicPrices: carAvailability });
    } catch (error) {
      console.error("Error fetching initial pricing:", error);
      setAvailabilityData({ dynamicPrices: [] });
    }
  };

  // Handle DatePicker Month Change
  const handleMonthChange = async (date) => {
    if (!selectedCar) return;

    try {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        category: selectedCar.category,
        includeAll: true,
      });

      const carAvailability =
        response.data.find((c) => c.stringId === selectedCar.stringId)
          ?.availability || [];
      setAvailabilityData({ dynamicPrices: carAvailability });
    } catch (error) {
      console.error("Error fetching pricing for month:", error);
    }
  };

  // Validate Dates
  const validateDates = (newDate, type) => {
    let isValid = true;
    let newErrors = { ...errors };

    if (type === "pickupDate") {
      if (rentalFormData.dropoffDate && newDate >= rentalFormData.dropoffDate) {
        newErrors.pickupDate = "Pick-up must be before drop-off";
        newErrors.dropoffDate = "Drop-off must be after pick-up";
        isValid = false;
      } else {
        delete newErrors.pickupDate;
        if (
          rentalFormData.dropoffDate &&
          newDate >= rentalFormData.dropoffDate
        ) {
          newErrors.dropoffDate = "Drop-off must be after pick-up";
        } else {
          delete newErrors.dropoffDate;
        }
      }
    } else if (type === "dropoffDate") {
      if (rentalFormData.pickupDate && newDate <= rentalFormData.pickupDate) {
        newErrors.dropoffDate = "Drop-off must be after pick-up";
        newErrors.pickupDate = "Pick-up must be before drop-off";
        isValid = false;
      } else {
        delete newErrors.dropoffDate;
        if (rentalFormData.pickupDate && newDate <= rentalFormData.pickupDate) {
          newErrors.pickupDate = "Pick-up must be before drop-off";
        } else {
          delete newErrors.pickupDate;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle Date Selection
  const handleDateChange = (date, type) => {
    if (!validateDates(date, type)) {
      return;
    }

    const isPickup = type === "pickupDate";
    const stateKey = isPickup ? "pickup" : "dropoff";
    const hasTime = date && (date.getHours() !== 0 || date.getMinutes() !== 0);

    setDateTimeComplete((prev) => ({ ...prev, [stateKey]: hasTime }));
    setTimeWarnings((prev) => ({ ...prev, [stateKey]: !hasTime }));

    setRentalFormData((prev) => ({ ...prev, [type]: date }));

    if (hasTime) {
      setDatePickerOpen((prev) => ({
        ...prev,
        [stateKey]: false,
        ...(isPickup && !rentalFormData.dropoffDate ? { dropoff: true } : {}),
      }));
      setErrors((prev) => ({ ...prev, [type]: undefined }));
    } else {
      setDatePickerOpen((prev) => ({ ...prev, [stateKey]: true }));
    }
  };

  // Calculate Price Breakdown
const calculatePriceBreakdown = useCallback(() => {
  if (
    !rentalFormData.pickupDate ||
    !rentalFormData.dropoffDate ||
    !selectedCar
  ) {
    return null;
  }

  const breakdown = [];
  let currentDate = new Date(rentalFormData.pickupDate);
  const endDate = new Date(rentalFormData.dropoffDate);

  // Use custom price if it's set and valid
  const customPriceStr = rentalFormData.customPricePerDay;
  const customPrice = customPriceStr !== "" && customPriceStr !== null && customPriceStr !== undefined
    ? parseFloat(customPriceStr)
    : null;

  // Only consider custom price valid if it's a positive number
  const isCustomPriceValid = customPrice !== null && !isNaN(customPrice) && customPrice > 0;

  // Keep original pickup and dropoff times for calculating if the last day is complete
  const exactPickupDate = new Date(currentDate);
  const exactDropoffDate = new Date(endDate);
  
  // Reset time for date-based comparisons in the loop
  let loopCurrentDate = new Date(currentDate);
  loopCurrentDate.setHours(0, 0, 0, 0);
  
  const loopEndDate = new Date(endDate);
  loopEndDate.setHours(23, 59, 59, 999); // End of day for comparison

  while (loopCurrentDate <= loopEndDate) {
    const dateString = loopCurrentDate.toDateString();

    // Get dynamic price if available and no valid custom price is set
    const dynamicPriceData = !isCustomPriceValid
      ? availabilityData?.dynamicPrices?.find(
          (p) => new Date(p.date).toDateString() === dateString
        )
      : null;

    // Determine price for the day (custom > dynamic > base)
    let price = selectedCar.basePrice; // Default to base
    if (isCustomPriceValid) {
      price = customPrice; // Use custom if valid
    } else if (dynamicPriceData?.dynamicPrice) {
      price = dynamicPriceData.dynamicPrice;
    }

    // Try to merge with previous period if same price
    const lastEntry = breakdown.length > 0 ? breakdown[breakdown.length - 1] : null;
    if (lastEntry && lastEntry.price === price && lastEntry.endDate.toDateString() === new Date(loopCurrentDate.getTime() - 86400000).toDateString()) {
      // If this is the same price and consecutive day, extend previous period
      lastEntry.days++;
      lastEntry.endDate = new Date(loopCurrentDate);
    } else {
      // Start new period
      breakdown.push({
        startDate: new Date(loopCurrentDate),
        endDate: new Date(loopCurrentDate),
        price,
        days: 1,
      });
    }

    // Move to next day
    loopCurrentDate = new Date(loopCurrentDate.setDate(loopCurrentDate.getDate() + 1));
  }

  // Adjust for partial days based on actual pickup and dropoff times
  // If the dropoff time is earlier than pickup time on the same day,
  // we should not charge for a full day
  if (breakdown.length > 0) {
    const lastPeriod = breakdown[breakdown.length - 1];
    const lastDayStart = new Date(lastPeriod.endDate);
    lastDayStart.setHours(0, 0, 0, 0);
    
    // If dropoff is on the same day as the last day in our breakdown
    if (lastDayStart.toDateString() === exactDropoffDate.toDateString()) {
      // Compare pickup and dropoff times with exact precision
      const pickupTime = exactPickupDate.getHours() * 60 + exactPickupDate.getMinutes();
      const dropoffTime = exactDropoffDate.getHours() * 60 + exactDropoffDate.getMinutes();
      
      // If times are exactly equal or dropoff time is earlier than pickup time
      if (dropoffTime < pickupTime || dropoffTime === pickupTime) {
        // Remove the last day if it's not a full rental day
        if (lastPeriod.days === 1) {
          breakdown.pop();
        } else {
          lastPeriod.days--;
          // If this was the end date of a multi-day period, adjust the end date
          lastPeriod.endDate = new Date(lastPeriod.endDate.setDate(lastPeriod.endDate.getDate() - 1));
        }
      }
    }
  }

  return breakdown;
}, [
  rentalFormData.pickupDate,
  rentalFormData.dropoffDate,
  selectedCar,
  availabilityData,
  rentalFormData.customPricePerDay
]);

  // Calculate Duration In 24 Hour Days
  const calculateDurationIn24HourDays = (pickupDateStr, dropoffDateStr) => {
    if (!pickupDateStr || !dropoffDateStr) {
      return 0;
    }

    const pickupDate = new Date(pickupDateStr);
    const dropoffDate = new Date(dropoffDateStr);

    if (isNaN(pickupDate.getTime()) || isNaN(dropoffDate.getTime())) {
      console.error("Invalid date provided for duration calculation.");
      return 0;
    }

    if (dropoffDate <= pickupDate) {
      if (dropoffDate.getTime() === pickupDate.getTime()) {
        return 0;
      }
      if (dropoffDate < pickupDate) {
        return 0;
      }
    }

    const diffTime = dropoffDate.getTime() - pickupDate.getTime();

    if (diffTime < 0) {
      return 0;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const pickupMinutes = pickupDate.getHours() * 60 + pickupDate.getMinutes();
    const dropoffMinutes =
      dropoffDate.getHours() * 60 + dropoffDate.getMinutes();
    const fullDays = Math.floor(diffTime / millisecondsPerDay);

    let extraDay = 0;

    if (dropoffMinutes > pickupMinutes) {
      extraDay = 1;
    } else if (dropoffMinutes === pickupMinutes) {
      const pickupSeconds = pickupDate.getSeconds();
      const dropoffSeconds = dropoffDate.getSeconds();
      if (dropoffSeconds > pickupSeconds) {
        extraDay = 1;
      }
    }

    return fullDays + extraDay;
  };

  // Calculate Base Total Price
  const calculateBaseTotalPrice = useCallback(() => {
    if (
      !rentalFormData.pickupDate ||
      !rentalFormData.dropoffDate ||
      !selectedCar
    ) {
      return selectedCar?.dynamicPrice || selectedCar?.basePrice || 0;
    }

    const breakdown = calculatePriceBreakdown();
    if (!breakdown)
      return selectedCar?.dynamicPrice || selectedCar?.basePrice || 0;

    const subtotal = breakdown.reduce(
      (total, period) => total + period.price * period.days,
      0
    );

    const totalDays = calculateDurationIn24HourDays(
      rentalFormData.pickupDate,
      rentalFormData.dropoffDate
    );

    let discountPercentage = 0;
    if (totalDays > 10) {
      discountPercentage = durationDiscounts.longTerm.discount;
    } else if (totalDays > 3) {
      discountPercentage = durationDiscounts.mediumTerm.discount;
    } else {
      discountPercentage = durationDiscounts.shortTerm.discount;
    }

    const durationDiscount = (subtotal * discountPercentage) / 100;

    let additionalOptionsCost = 0;
    if (additionalOptionsData.length > 0 && rentalFormData.additionalOptions) {
      additionalOptionsData.forEach((option) => {
        if (rentalFormData.additionalOptions[option._id]) {
          additionalOptionsCost += option.price * totalDays;
        }
      });
    }

    return subtotal - durationDiscount + additionalOptionsCost;
  }, [
    rentalFormData.pickupDate,
    rentalFormData.dropoffDate,
    selectedCar,
    durationDiscounts,
    calculatePriceBreakdown,
    rentalFormData.additionalOptions,
    additionalOptionsData,
  ]);

  // useEffect for Final Price Calculation
  useEffect(() => {
    if (
      rentalFormData.pickupDate &&
      rentalFormData.dropoffDate &&
      selectedCar
    ) {
      const baseTotal = calculateBaseTotalPrice();
      const netTotal = baseTotal / 1.24;
      const applicableDiscount = couponStatus.isValid
        ? Math.min(couponStatus.discount, netTotal)
        : 0;
      const netAfterDiscount = netTotal - applicableDiscount;
      const finalTotal = netAfterDiscount * 1.24;
      setTotalPrice(finalTotal >= 0 ? finalTotal : 0);
    } else {
      setTotalPrice(0);
    }
  }, [
    rentalFormData.pickupDate,
    rentalFormData.dropoffDate,
    selectedCar,
    calculateBaseTotalPrice,
    couponStatus.isValid,
    couponStatus.discount,
  ]);

  // Format Date Range for Display
  const formatDateRange = (date) => {
    return moment(date).format("DD/MM");
  };

  // Render Day Contents in DatePicker
  const renderDayContents = (day, date) => {
    if (!selectedCar || !availabilityData?.dynamicPrices) {
      return <span>{day}</span>;
    }

    const dateString = date.toDateString();
    const dayData = availabilityData.dynamicPrices.find(
      (p) => new Date(p.date).toDateString() === dateString
    );

    const customPriceStr = rentalFormData.customPricePerDay;
    const customPrice =
      customPriceStr !== "" &&
      customPriceStr !== null &&
      customPriceStr !== undefined
        ? parseFloat(customPriceStr)
        : null;
    const isCustomPriceValid =
      customPrice !== null && !isNaN(customPrice) && customPrice > 0;

    let displayPrice = selectedCar.basePrice;
    let priceColor = "text-gray-500";

    if (isCustomPriceValid) {
      displayPrice = customPrice;
      priceColor = "text-yellow-600";
    } else if (dayData?.dynamicPrice) {
      displayPrice = dayData.dynamicPrice;
      priceColor =
        displayPrice !== selectedCar.basePrice
          ? "text-blue-600"
          : "text-gray-500";
    }

    return (
      <div className="text-center">
        <div>{day}</div>
        <div className={`text-xs ${priceColor} font-medium`}>
          €{displayPrice}
        </div>
      </div>
    );
  };

  // Validate Coupon
  const validateCoupon = async (code) => {
    if (!code.trim()) {
      setCouponStatus({ isValid: false, discount: 0, message: "" });
      return;
    }

    try {
      const baseTotal = calculateBaseTotalPrice();
      const netTotal = baseTotal / 1.24;
      const response = await coupons.validate(code, netTotal);

      setCouponStatus({
        isValid: true,
        discount: response.data.discount,
        message: `Coupon applied! You save €${response.data.discount.toFixed(
          2
        )}`,
      });
    } catch (error) {
      setCouponStatus({
        isValid: false,
        discount: 0,
        message: "Invalid coupon code",
      });
    }
  };

  // Handover form functions
  const fetchAvailableVehicles = async () => {
    try {
      console.log("Fetching available vehicles for car:", selectedCar);
      const carCategory = selectedCar.category;

      const sameVehicleCategorySelected = await cars.getVehiclesFromCategory(
        carCategory
      );
      console.log(
        "Fetched vehicles from same category:",
        sameVehicleCategorySelected.data
      );

      setAvailableVehicles(sameVehicleCategorySelected.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setAvailableVehicles([]);
    }
  };

  // Enhanced handover form functions
  const handleHandoverInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setHandoverFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setHandoverFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear step errors when user starts typing
    if (handoverStepErrors[`step${handoverCurrentStep}`]) {
      setHandoverStepErrors((prev) => ({
        ...prev,
        [`step${handoverCurrentStep}`]: null,
      }));
    }
  };

  const handleVehicleSelection = (e) => {
    setSelectedVehicleId(e.target.value);
    // Clear step 1 error when vehicle is selected
    if (e.target.value && handoverStepErrors.step1) {
      setHandoverStepErrors((prev) => ({ ...prev, step1: null }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setMediaFiles((prev) => [...prev, ...files]);
  };

  const captureMedia = (type) => {
    setCapturing(true);
    // Implementation for camera capture would go here
    // For now, just trigger file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setCapturing(false);
  };

  const addDamage = () => {
    setHandoverFormData((prev) => ({
      ...prev,
      damages: [
        ...prev.damages,
        { description: "", severity: "minor", location: "" },
      ],
    }));
  };

  const updateDamage = (index, field, value) => {
    setHandoverFormData((prev) => ({
      ...prev,
      damages: prev.damages.map((damage, i) =>
        i === index ? { ...damage, [field]: value } : damage
      ),
    }));
  };

  const removeDamage = (index) => {
    setHandoverFormData((prev) => ({
      ...prev,
      damages: prev.damages.filter((_, i) => i !== index),
    }));
  };

  const openSignatureModal = (type) => {
    setSigningMode(type);
    setShowSignatureModal(true);
  };

  const clearSignature = () => {
    if (signingMode === "employee" && employeeSignatureRef.current) {
      employeeSignatureRef.current.clear();
    } else if (signingMode === "customer" && customerSignatureRef.current) {
      customerSignatureRef.current.clear();
    }
  };
  const saveSignature = async () => {
    let signatureCanvas;

    if (signingMode === "employee") {
      signatureCanvas = employeeSignatureRef.current;
    } else if (signingMode === "customer") {
      signatureCanvas = customerSignatureRef.current;
    }

    if (signatureCanvas && !signatureCanvas.isEmpty()) {
      try {
        setSignatureLoading((prev) => ({ ...prev, [signingMode]: true }));
        const dataURL = signatureCanvas.toDataURL("image/png");
        const response = await axios.post(
          "https://rent-server.create-site.gr/api/signatures/upload-base64",
          {
            signature: dataURL,
          }
        );

        if (response.data.success && response.data.filePath) {
          const signatureUrl = `https://rent-server.create-site.gr${response.data.filePath}`;
          setSignatureData((prev) => ({
            ...prev,
            [signingMode]: signatureUrl,
          }));
          setShowSignatureModal(false);
        } else {
          setError(
            "Failed to save signature: " +
              (response.data.message || "Server error or missing filePath")
          );
        }
      } catch (err) {
        console.error("Error saving signature:", err);
        setError("Error saving signature: " + (err.message || "Unknown error"));
      } finally {
        setSignatureLoading((prev) => ({ ...prev, [signingMode]: false }));
      }
    }
  };

  const cancelSignature = () => {
    setShowSignatureModal(false);
  };

  const handoverNextStep = () => {
    if (validateHandoverStep(handoverCurrentStep)) {
      setHandoverCurrentStep((prev) => Math.min(prev + 1, handoverTotalSteps));
    }
  };

  const handoverPrevStep = () => {
    setHandoverCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const validateHandoverStep = (step) => {
    let isValid = true;
    let errorMessage = null;

    switch (step) {
      case 1:
        if (!selectedVehicleId) {
          errorMessage = "Please select a vehicle";
          isValid = false;
        }
        break;      case 2:
        // Mileage is now optional, but if provided, must be valid
        if (handoverFormData.mileage && (
          isNaN(handoverFormData.mileage) ||
          handoverFormData.mileage < 0
        )) {
          errorMessage = "Please enter a valid mileage";
          isValid = false;
        }
        if (!handoverFormData.fuelLevel) {
          errorMessage = "Please enter the current fuel level";
          isValid = false;
        }
        break;
      case 3:
        // Documentation step is optional
        break;
      case 4:
        if (!signatureData.employee || (!signatureData.customer && !isAdmin)) {
          errorMessage = "Both employee and customer signatures are required";
          isValid = false;
        }
        if (!handoverFormData.confirmed) {
          errorMessage = "Please confirm the handover details";
          isValid = false;
        }

        if (isAdmin) {
          setHandoverStepErrors((prev) => ({ ...prev, step4: null }));
          return true;
        }
        break;
      default:
        break;
    }

    setHandoverStepErrors((prev) => ({
      ...prev,
      [`step${step}`]: errorMessage,
    }));

    if (errorMessage) {
      toast.error(errorMessage);
    }

    return isValid;
  };

  const renderHandoverStepError = (step) => {
    const error = handoverStepErrors[`step${step}`];
    if (!error) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
      >
        <div className="flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      </motion.div>
    );
  };

  const renderHandoverStep = () => {
    switch (handoverCurrentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Basic Information
              </h4>
              <p className="text-gray-600">
                Select the vehicle and set basic handover details
              </p>
            </div>
            {renderHandoverStepError(1)}
            {/* Handover Type */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Handover Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="handoverType"
                    value="pickup"
                    checked={handoverFormData.handoverType === "pickup"}
                    onChange={handleHandoverInputChange}
                    className="mr-2"
                  />
                  <span>Vehicle Pickup</span>
                </label>
                {/* <label className="flex items-center">
                  <input
                    type="radio"
                    name="handoverType"
                    value="return"
                    checked={handoverFormData.handoverType === "return"}
                    onChange={handleHandoverInputChange}
                    className="mr-2"
                  />
                  <span>Vehicle Return</span>
                </label> */}
              </div>
            </div>
            {/* Employee Selection for Admin Users */}
            {isAdmin && (
              <motion.div variants={itemVariants} className="mb-6">
                <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
                  <FaUser className="mr-2 text-purple-600" />
                  Assign Employee
                </label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select an employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} -{" "}
                      {employee.email}
                    </option>
                  ))}
                </select>
                {isAdmin && !selectedEmployeeId && (
                  <p className="text-red-500 text-sm mt-1">
                    Please select an employee for this handover
                  </p>
                )}
              </motion.div>
            )}
            {/* Vehicle Selection */}{" "}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Vehicle <span className="text-red-500">*</span>
              </label>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Search by license plate..."
                value={vehicleSearchQuery}
                onChange={(e) => setVehicleSearchQuery(e.target.value)}
                className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />

              <select
                value={selectedVehicleId}
                onChange={handleVehicleSelection}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  handoverStepErrors.step1
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Choose a vehicle</option>
                {Array.isArray(availableVehicles) &&
                availableVehicles.length > 0 ? (
                  availableVehicles
                    .filter(
                      (vehicle) =>
                        vehicle &&
                        vehicle.licensePlate &&
                        (!vehicleSearchQuery ||
                          vehicle.licensePlate
                            .toLowerCase()
                            .includes(vehicleSearchQuery.toLowerCase()))
                    )
                    .map((vehicle) => (
                      <option
                        key={vehicle._id || vehicle.vin}
                        value={vehicle._id || vehicle.vin}
                      >
                        {vehicle.licensePlate || "Unknown Plate"} -{" "}
                        {vehicle.vin || "Unknown VIN"}
                        {vehicle.insurance && vehicle.insurance.provider
                          ? ` (Insured: ${vehicle.insurance.provider})`
                          : " (No Insurance)"}
                      </option>
                    ))
                ) : (
                  <option disabled>No vehicles available</option>
                )}
              </select>

              {/* Vehicle Details */}
              {selectedVehicleId && (
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  {(() => {
                    const selectedVehicle = availableVehicles.find(
                      (v) => v._id === selectedVehicleId
                    );
                    if (!selectedVehicle) return null;

                    return (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">License Plate:</span>
                          <span>{selectedVehicle.licensePlate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">VIN:</span>
                          <span>{selectedVehicle.vin}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Condition:</span>
                          <span className="capitalize">
                            {selectedVehicle.condition}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Current Mileage:</span>
                          <span>{selectedVehicle.currentMileage} km</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Insurance:</span>
                          <span
                            className={
                              selectedVehicle.insurance
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {selectedVehicle.insurance
                              ? `${selectedVehicle.insurance.provider} (${selectedVehicle.insurance.policyNumber})`
                              : "No Insurance"}
                          </span>
                        </div>
                        {selectedVehicle.specialNotes && (
                          <div className="flex justify-between">
                            <span className="font-medium">Notes:</span>
                            <span className="text-gray-600">
                              {selectedVehicle.specialNotes}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {availableVehicles.length === 0 && (
                <p className="text-sm text-yellow-600 mt-2">
                  No vehicles available. Please check if vehicles are properly
                  configured for this car model.
                </p>
              )}

              {vehicleSearchQuery &&
                availableVehicles.filter((vehicle) =>
                  vehicle.licensePlate
                    .toLowerCase()
                    .includes(vehicleSearchQuery.toLowerCase())
                ).length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    No vehicles found matching "{vehicleSearchQuery}"
                  </p>
                )}
            </div>
            {/* Rental Information Display */}
            {createdRental && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                  <FaClipboardList className="mr-2" />
                  Rental Information
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Customer:</span>
                    <span className="ml-2">
                      {createdRental.customer?.firstName}{" "}
                      {createdRental.customer?.lastName}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Vehicle:</span>
                    <span className="ml-2">
                      {createdRental.carName || createdRental.car}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Pickup:</span>
                    <span className="ml-2">
                      {moment(createdRental.pickupDateTime).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Return:</span>
                    <span className="ml-2">
                      {moment(createdRental.returnDateTime).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Vehicle Condition
              </h4>
              <p className="text-gray-600">
                Record the current condition of the vehicle
              </p>
            </div>

            {renderHandoverStepError(2)}

            {/* Vehicle Condition Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaTachometerAlt className="inline mr-2" />
                  Current Mileage
                </label>
                <input
                  type="number"
                  name="mileage"
                  value={handoverFormData.mileage}
                  onChange={handleHandoverInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    handoverStepErrors.step2
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter current mileage (optional)"
                  min="0"
                />
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaGasPump className="inline mr-2" />
                  Fuel Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="fuelLevel"
                  value={handoverFormData.fuelLevel}
                  required
                  onChange={handleHandoverInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select fuel level</option>
                  <option value="empty">Empty</option>
                  <option value="1/8">1/8 Tank</option>
                  <option value="2/8">2/8 Tank</option>
                  <option value="3/8">3/8 Tank</option>
                  <option value="4/8">4/8 Tank</option>
                  <option value="5/8">5/8 Tank</option>
                  <option value="6/8">6/8 Tank</option>
                  <option value="7/8">7/8 Tank</option>
                  <option value="full">Full Tank</option>
                </select>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaCar className="inline mr-2" />
                  Exterior Condition
                </label>
                <select
                  name="condition.exterior"
                  value={handoverFormData.condition.exterior}
                  onChange={handleHandoverInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
            </div>

            {/* Interior Condition */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interior Condition
              </label>
              <select
                name="condition.interior"
                value={handoverFormData.condition.interior}
                onChange={handleHandoverInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>

            {/* Condition Notes */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition Notes
              </label>
              <textarea
                name="condition.notes"
                value={handoverFormData.condition.notes}
                onChange={handleHandoverInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Any specific notes about the vehicle condition..."
              />
            </div>

            {/* Damages Section */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Damages/Issues
                </label>
                <button
                  type="button"
                  onClick={addDamage}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center"
                >
                  <FaPlus className="mr-1" />
                  Add Damage
                </button>
              </div>

              {handoverFormData.damages.length === 0 ? (
                <div className="text-center py-4 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                  No damages recorded. Click "Add Damage" to report any issues.
                </div>
              ) : (
                <div className="space-y-3">
                  {handoverFormData.damages.map((damage, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Damage description"
                          value={damage.description}
                          onChange={(e) =>
                            updateDamage(index, "description", e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                          value={damage.severity}
                          onChange={(e) =>
                            updateDamage(index, "severity", e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="minor">Minor</option>
                          <option value="moderate">Moderate</option>
                          <option value="major">Major</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Location on vehicle"
                          value={damage.location}
                          onChange={(e) =>
                            updateDamage(index, "location", e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDamage(index)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                      >
                        <FaTrash className="mr-1" />
                        Remove
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Documentation
              </h4>
              <p className="text-gray-600">
                Upload photos and videos of the vehicle
              </p>
            </div>

            {/* Media Upload Section */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h5 className="font-medium text-gray-900 mb-4 flex items-center">
                <FaCamera className="mr-2" />
                Photos and Videos
              </h5>

              {/* Upload Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => captureMedia("photo")}
                  disabled={capturing}
                  className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <FaCamera className="mr-2" />
                  {capturing ? "Capturing..." : "Take Photo"}
                </button>

                <label className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer">
                  <FaUpload className="mr-2" />
                  Upload Files
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Media Preview */}
              {mediaFiles.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h6 className="font-medium text-gray-900 mb-2">
                    Uploaded Files ({mediaFiles.length})
                  </h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {mediaFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                          {file.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <FaCamera className="text-gray-500 text-2xl" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {file.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mediaFiles.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <FaCamera className="mx-auto text-4xl text-gray-400 mb-2" />
                  <p className="text-gray-500">No files uploaded yet</p>
                  <p className="text-sm text-gray-400">
                    Take photos or upload files to document the vehicle
                    condition
                  </p>
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handover Notes
              </label>
              <textarea
                name="notes"
                value={handoverFormData.notes}
                onChange={handleHandoverInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional notes about the handover process, special instructions, or observations..."
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Signatures & Confirmation
              </h4>
              <p className="text-gray-600">
                Collect signatures and confirm the handover
              </p>
            </div>

            {renderHandoverStepError(4)}

            {/* Admin notification about signatures */}
            {isAdmin && (
              <motion.div
                variants={itemVariants}
                className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div className="flex items-center">
                  <FaInfoCircle className="text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium">Admin Note:</span>
                </div>
                <p className="text-blue-700 mt-1">
                  As an administrator, signatures are not required for handovers
                  you create. You can proceed directly to submit the handover.
                </p>
              </motion.div>
            )}

            {/* Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Employee Signature <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center min-h-[120px] flex items-center justify-center">
                  {signatureData.employee ? (
                    <div className="w-full">
                      <img
                        src={signatureData.employee}
                        alt="Employee signature"
                        className="max-h-20 mx-auto mb-2 border rounded"
                      />
                      <button
                        type="button"
                        onClick={() => openSignatureModal("employee")}
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Update Signature
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openSignatureModal("employee")}
                      className="flex flex-col items-center justify-center w-full h-full text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <FaSignature className="text-2xl mb-2" />
                      <span className="text-sm">Click to Sign</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Customer Signature <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center min-h-[120px] flex items-center justify-center">
                  {signatureData.customer ? (
                    <div className="w-full">
                      <img
                        src={signatureData.customer}
                        alt="Customer signature"
                        className="max-h-20 mx-auto mb-2 border rounded"
                      />
                      <button
                        type="button"
                        onClick={() => openSignatureModal("customer")}
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Update Signature
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openSignatureModal("customer")}
                      className="flex flex-col items-center justify-center w-full h-full text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <FaSignature className="text-2xl mb-2" />
                      <span className="text-sm">Click to Sign</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Handover Summary */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-blue-900 mb-3">
                Handover Summary
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p>
                    <span className="font-medium">Vehicle:</span>{" "}
                    {availableVehicles.find((v) => v._id === selectedVehicleId)
                      ?.licensePlate || "Not selected"}
                  </p>
                  <p>
                    <span className="font-medium">Mileage:</span>{" "}
                    {handoverFormData.mileage || "Not recorded"} km
                  </p>
                  <p>
                    <span className="font-medium">Fuel Level:</span>{" "}
                    {handoverFormData.fuelLevel}
                  </p>
                </div>
                <div>
                  <p>
                    <span className="font-medium">Exterior:</span>{" "}
                    {handoverFormData.condition.exterior}
                  </p>
                  <p>
                    <span className="font-medium">Interior:</span>{" "}
                    {handoverFormData.condition.interior}
                  </p>
                  <p>
                    <span className="font-medium">Damages:</span>{" "}
                    {handoverFormData.damages.length} reported
                  </p>
                </div>
              </div>
            </div>

            {/* Final Confirmation */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="confirmed"
                  checked={handoverFormData.confirmed}
                  onChange={handleHandoverInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that all the information above is accurate and the
                  vehicle has been handed over in the stated condition. Both
                  parties have reviewed and agreed to the handover details.
                </span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSubmitHandover = async () => {
    if (!validateHandoverStep(4)) return;

    setLoading(true);
    try {
      // Prepare the handover data
      const handoverData = {
        rental: createdRental._id,
        handoverType: handoverFormData.handoverType,
        vehicleInstanceId: selectedVehicleId,
        mileage: handoverFormData.mileage,
        fuelLevel: handoverFormData.fuelLevel,
        condition: {
          exterior: handoverFormData.condition.exterior,
          interior: handoverFormData.condition.interior,
          notes: handoverFormData.condition.notes,
        },
        damages: handoverFormData.damages,
        notes: handoverFormData.notes,
        signatures: {
          employee: signatureData.employee,
          customer: signatureData.customer,
        },
        customer: createdRental
          ? {
              name: `${createdRental.customer.firstName} ${createdRental.customer.lastName}`,
              email: createdRental.customer.email,
              phoneNumber:
                createdRental.customer.phoneNumber ||
                createdRental.customer.phone,
            }
          : null,
        // Handle employee assignment based on user type
        employeeId: isAdmin
          ? selectedEmployeeId
          : localStorage.getItem("employeeId"),
        // Add admin-specific fields
        ...(isAdmin && {
          createdBy: "admin",
          assignedEmployee: selectedEmployeeId,
        }),
      };

      console.log("Submitting handover data:", handoverData);

      // Create the handover
      const response = await handoverService.createHandover(handoverData);
      console.log("Handover created successfully:", response.data);
      const handoverId = response.data._id;

      // Upload any media files using the correct endpoint
      if (mediaFiles.length > 0) {
        try {
          console.log(
            `Starting upload of ${mediaFiles.length} media files to handover ${handoverId}`
          );

          // Group files by type
          const imageFiles = mediaFiles.filter((file) =>
            file.type.startsWith("image/")
          );
          const videoFiles = mediaFiles.filter((file) =>
            file.type.startsWith("video/")
          );
          const audioFiles = mediaFiles.filter((file) =>
            file.type.startsWith("audio/")
          );

          console.log("Files grouped by type:", {
            images: imageFiles.length,
            videos: videoFiles.length,
            audio: audioFiles.length,
          });

          // Upload each file individually using the correct service method
          let uploadCount = 0;

          // Upload images
          for (const file of imageFiles) {
            const imageFormData = new FormData();
            imageFormData.append("file", file);
            imageFormData.append("documentType", "photo");
            imageFormData.append(
              "caption",
              `${file.name} - ${moment().format("DD/MM/YYYY HH:mm")}`
            );

            console.log(
              `Uploading photo ${uploadCount + 1}/${mediaFiles.length}: ${
                file.name
              }`
            );
            await handoverService.addDocumentation(handoverId, imageFormData);
            uploadCount++;

            // Add small delay between uploads
            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          // Upload videos
          for (const file of videoFiles) {
            const videoFormData = new FormData();
            videoFormData.append("file", file);
            videoFormData.append("documentType", "video");
            videoFormData.append(
              "caption",
              `${file.name} - ${moment().format("DD/MM/YYYY HH:mm")}`
            );

            console.log(
              `Uploading video ${uploadCount + 1}/${mediaFiles.length}: ${
                file.name
              }`
            );
            await handoverService.addDocumentation(handoverId, videoFormData);
            uploadCount++;

            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          // Upload audio files
          for (const file of audioFiles) {
            const audioFormData = new FormData();
            audioFormData.append("file", file);
            audioFormData.append("documentType", "voiceNote");
            audioFormData.append(
              "caption",
              `${file.name} - ${moment().format("DD/MM/YYYY HH:mm")}`
            );

            console.log(
              `Uploading audio ${uploadCount + 1}/${mediaFiles.length}: ${
                file.name
              }`
            );
            await handoverService.addDocumentation(handoverId, audioFormData);
            uploadCount++;

            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          console.log(`Successfully uploaded ${uploadCount} files`);
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          setError("Some files could not be uploaded: " + uploadError.message);
        }
      }

      toast.success("Handover completed successfully!");

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Error submitting handover:", error);
      toast.error(error.response?.data?.message || "Failed to submit handover");
    } finally {
      setLoading(false);
    }
  };

  const renderHandoverForm = () => {
    return (
      <div className="space-y-6">
        {/* Progress Steps */}
        <div className="relative mb-8">
          <div className="flex justify-between mb-2 relative z-10">
            {[1, 2, 3, 4].map((step) => (
              <motion.div
                key={step}
                whileHover={{ scale: handoverCurrentStep >= step ? 1.05 : 1 }}
                onClick={() =>
                  handoverCurrentStep >= step && setHandoverCurrentStep(step)
                }
                className={`flex flex-col items-center cursor-pointer ${
                  handoverCurrentStep >= step ? "" : "opacity-60"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
                    handoverCurrentStep === step
                      ? "bg-blue-600 text-white"
                      : handoverCurrentStep > step
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {handoverCurrentStep > step ? (
                    <FaCheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-lg">{step}</span>
                  )}
                </div>
                <span className="text-xs mt-2 font-medium">
                  {step === 1 && "Basic Info"}
                  {step === 2 && "Vehicle Condition"}
                  {step === 3 && "Documentation"}
                  {step === 4 && "Signatures"}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="absolute top-5 left-0 w-full h-1 bg-gray-200 z-0">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${
                  ((handoverCurrentStep - 1) * 100) / (handoverTotalSteps - 1)
                }%`,
              }}
              transition={{ duration: 0.3 }}
              className="h-full bg-blue-600"
            />
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={handoverCurrentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderHandoverStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  };

  // Enhanced resetForm to include handover reset
  const resetForm = () => {
    setCurrentStep(1);
    setCreatedRental(null);
    setSelectedCar(null);
    setAvailabilityData(null);
    setCouponStatus({ isValid: false, discount: 0, message: "" });
    setErrors({});
    setTotalPrice(0);
    setRentalFormData({
      pickupDate: null,
      dropoffDate: null,
      pickupLocation: "",
      dropoffLocation: "",
      customPickupLocation: "",
      customDropoffLocation: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "26+",
      additionalOptions: {},
      couponCode: "",
      notes: "",
      customPricePerDay: "",
      advancePayment: "",
      paymentMethod: "",
    });
    setSameLocation(true);
    setDatePickerOpen({ pickup: false, dropoff: false });
    setDateTimeComplete({ pickup: false, dropoff: false });
    setTimeWarnings({ pickup: false, dropoff: false });
    setSearchQuery("");
    setSelectedCategory("");

    // Reset handover form
    setHandoverCurrentStep(1);
    setHandoverStepErrors({
      step1: null,
      step2: null,
      step3: null,
      step4: null,
    });
    setHandoverFormData({
      handoverType: "pickup",
      mileage: "",
      fuelLevel: "full",
      condition: {
        exterior: "good",
        interior: "good",
        notes: "",
      },
      notes: "",
      damages: [],
      confirmed: false,
    });
    setSelectedVehicleId("");
    setAvailableVehicles([]);
    setMediaFiles([]);
    setSignatureData({ employee: "", customer: "" });
    setCapturing(false);
    setShowSignatureModal(false);
    setSigningMode(null);
    setSignatureLoading({ employee: false, customer: false });

    setAdditionalOptionsData([]);
    setLoadingAdditionalOptions(false);

        // Reset invoice state
    setWantsInvoice(false);
    setInvoiceData({
      companyName: "",
      vatNumber: "",
      address: "",
      city: "",
      postalCode: "",
      country: "Greece"
    });
  };

  if (!isOpen) return null;

  const handleCreateRental = async () => {
    console.log("Creating rental with data:", rentalFormData);
      if (createdRental) {
    setCurrentStep(2);
    return;
  }
    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      console.error(
        `Validation failed for field: ${firstErrorField}`
      )
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"], #${firstErrorField}`
        );
        console.error(
          `Validation failed for field: ${firstErrorField}`
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setLoading(true);
    try {
      // Calculate price breakdown in the format expected by the server
      const priceBreakdown = calculatePriceBreakdown();
      if (!priceBreakdown || priceBreakdown.length === 0) {
        throw new Error("Unable to calculate price breakdown");
      }

      // Transform price breakdown to match server expectations
      const transformedPriceBreakdown = (priceBreakdown || []).map(period => ({
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString(),
        pricePerDay: period.pricePerDay || period.price || 0,
        numberOfDays: period.numberOfDays || period.days || 1,
        totalForPeriod: ((period.pricePerDay || 0) * (period.numberOfDays || 1) || period.price || 0) * (period.days || 1),
      }));

      // Calculate total rental days for additional options
      const totalRentalDays = calculateDurationIn24HourDays(
        rentalFormData.pickupDate,
        rentalFormData.dropoffDate
      );

      // Prepare rental data with correct field names
      const rentalData = {
        customer: {
          firstName: rentalFormData.firstName,
          lastName: rentalFormData.lastName,
          email: rentalFormData.email,
          phoneNumber: rentalFormData.phone,
          age: rentalFormData.age,
        },
        car: selectedCar.stringId,
        pickupDateTime: rentalFormData.pickupDate.toISOString(),
        returnDateTime: rentalFormData.dropoffDate.toISOString(),
        pickupLocation:
          rentalFormData.pickupLocation === "other"
            ? rentalFormData.customPickupLocation
            : getLocationNameFromId(rentalFormData.pickupLocation),
        returnLocation: sameLocation
          ? rentalFormData.pickupLocation === "other"
            ? rentalFormData.customPickupLocation
            : getLocationNameFromId(rentalFormData.pickupLocation)
          : rentalFormData.dropoffLocation === "other"
          ? rentalFormData.customDropoffLocation
          : getLocationNameFromId(rentalFormData.dropoffLocation),
        totalPrice: totalPrice,
        basePrice: selectedCar.basePrice || 0,
        advancePayment: rentalFormData.advancePayment
          ? parseFloat(rentalFormData.advancePayment)
          : 0,
        paymentMethod: rentalFormData.paymentMethod,
        invoiceType: wantsInvoice ? "invoice" : "receipt",
        invoiceData: wantsInvoice ? invoiceData : undefined,
        additionalNotes: rentalFormData.notes,
        additionalOptions: Object.keys(rentalFormData.additionalOptions)
          .filter((key) => rentalFormData.additionalOptions[key])
          .map((key) => {
            const option = additionalOptionsData.find((opt) => opt._id === key);
            return {
              name: option?.name || "Unknown Option",
              price: option?.price || 0,
            };
          }),
        couponCode: rentalFormData.couponCode || "",
        priceBreakdown: transformedPriceBreakdown,
        customPricePerDay: rentalFormData.customPricePerDay
          ? parseFloat(rentalFormData.customPricePerDay)
          : null,
        status: "confirmed",
        totalRentalDays: totalRentalDays, // Add this for additional options calculation
      };

      console.log("Sending rental data:", rentalData);

        console.log("Price breakdown debug:", {
    totalPrice,
    selectedCar,
    rentalFormData: {
      pickupDate: rentalFormData.pickupDate,
      dropoffDate: rentalFormData.dropoffDate,
      additionalOptions: rentalFormData.additionalOptions
    },
    priceBreakdown: calculatePriceBreakdown(),
    baseTotalPrice: calculateBaseTotalPrice(),
    couponStatus,
    additionalOptionsData
  });

            const pdfData = await generateInvoice(rentalData);
      rentalData.invoicePdf = pdfData;

      // Create rental
      const response = await rentals.create(rentalData);
      const rental = response.data;

      setCreatedRental(rental);
      // Fetch available vehicles for the selected car
      await fetchAvailableVehicles(selectedCar);

      toast.success(
        "Rental created successfully! Now complete the pickup handover."
      );
      setCurrentStep(2);
    } catch (error) {
      console.error("Error creating rental:", error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error("Car is not available for the selected dates");
        setErrors({ car: "Selected car is not available for these dates" });
      } else if (error.response?.status === 400) {
        // Log the detailed error for debugging
        console.error("Validation errors:", error.response?.data?.errors);
        const errorMessage =
          error.response?.data?.message ||
          "Invalid rental data. Please check all fields.";
        toast.error(errorMessage);

        // Handle validation errors
        if (error.response?.data?.errors) {
          const validationErrors = {};
          error.response.data.errors.forEach((err) => {
            validationErrors[err.path || err.param] = err.message || err.msg;
          });
          setErrors(validationErrors);
        }
      } else {
        toast.error("Failed to create rental. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-[1400px] max-h-[100vh] overflow-hidden"
        >
          {/* Header */}{" "}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {currentStep === 1
                    ? "Create Rental & Handover"
                    : "Complete Pickup Handover"}
                </h2>
                {selectedDate && currentStep === 1 && (
                  <p className="text-sm text-gray-600 mt-1">
                    For{" "}
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 1
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {currentStep > 1 ? <FaCheck /> : "1"}
                </div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 2
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  2
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {initialLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {currentStep === 1 && renderRentalForm()}
                {currentStep === 2 && renderHandoverForm()}
              </>
            )}
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <div className="flex items-center space-x-3">
              {currentStep === 2 && (
                <button
                  onClick={() => setCurrentStep(1)}
                  disabled={loading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors flex items-center"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to Rental
                </button>
              )}
              {currentStep === 2 && handoverCurrentStep > 1 && (
                <button
                  onClick={handoverPrevStep}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                >
                  <FaArrowLeft className="mr-2" />
                  Previous
                </button>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              {currentStep === 1 ? (
                <button
                  onClick={handleCreateRental}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaArrowRight className="mr-2" />
                  )}
                  Create & Continue
                </button>
              ) : handoverCurrentStep < handoverTotalSteps ? (
                <button
                  onClick={handoverNextStep}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Next
                  <FaArrowRight className="ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleSubmitHandover}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  )}
                  Complete Handover
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Signature Modal */}
        {showSignatureModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-60"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-lg w-full"
            >
              <div className="p-6">
                <h3 className="text-lg font-medium mb-4">
                  {signingMode === "employee" ? "Employee" : "Customer"}{" "}
                  Signature
                </h3>
                <div className="border border-gray-300 rounded">
                  <SignatureCanvas
                    ref={
                      signingMode === "employee"
                        ? employeeSignatureRef
                        : customerSignatureRef
                    }
                    canvasProps={{
                      width: 450,
                      height: 200,
                      className: "signature-canvas w-full",
                    }}
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={clearSignature}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Clear
                  </button>
                  <button
                    onClick={cancelSignature}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSignature}
                    disabled={signatureLoading[signingMode]}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {signatureLoading[signingMode] ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default CombinedRentalHandoverModal;
